import { LitElement, html, css, nothing, svg } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import SunCalc from 'suncalc'
import {
  area,
  polygon,
  multiPolygon,
  lineString,
  multiLineString,
  length as turfLength,
} from '@turf/turf'
import type maplibregl from 'maplibre-gl'
import type {
  CurrentConditions,
  SkyCondition,
  WeatherData,
  LightningRisk,
} from '../../services/weather/weather-types.js'
import { capeToLightningRisk } from '../../services/weather/weather-utils.js'
import { WeatherService } from '../../services/weather/weather-service.js'
import { reverseGeocodeService } from '../../services/geocode/reverse-geocode-service.js'
import type { ReverseGeocodeResult } from '../../services/geocode/reverse-geocode-service.js'
import { formatPosition } from '../../lib/coordinates.js'
import type { CoordinateFormat, CoordinatePrecision, Position } from '../../lib/coordinates.js'
import { convertElevation, convertDistance, convertArea } from '../../utils/units.js'
import type { UnitSystem } from '../../utils/units.js'
import { SettingsStorage } from '../../storage/settings-storage.js'
import { elevationPointService } from '../../services/terrain/elevation-point-service.js'
import type {
  EmergencyDataType,
  AnyEmergencyFacility,
} from '../../services/emergency/emergency-types.js'
import type { INatObservation, NPSAlert } from '../../services/wildlife/wildlife-types.js'
import { snowDepthService } from '../../services/terrain/snow-depth-service.js'
import { timelineService } from '../../services/timeline/timeline-service.js'
import { CARRIER_COLORS } from '../../services/cellular/carrier-roaming-map.js'
import { openBetaSearchService } from '../../services/openbeta/openbeta-search-service.js'
import type { ClimbFilterOptions } from '../../services/openbeta/openbeta-search-service.js'
import type { OpenBetaAreaDetail, OpenBetaClimb } from '../../services/openbeta/openbeta-types.js'
import {
  parseGradeNumeric,
  V_OFFSET,
  CLIMB_TYPES,
} from '../../services/openbeta/openbeta-grades.js'
import geomagnetism from 'geomagnetism'
import {
  avalancheLayerService,
  type AvalancheZoneProperties,
} from '../../services/avalanche/avalanche-layer-service.js'
import type { AvalancheProblemType, DangerLevel } from '../../services/avalanche/avalanche-types.js'
import { dangerLabel, dangerColor, problemLabel } from '../../services/avalanche/avalanche-utils.js'
import { fireLayerService, firmsConfidenceLabel } from '../../services/fire/fire-layer-service.js'
import type { FireFeatureHit } from '../../services/fire/fire-layer-service.js'
import { npsAlertsService } from '../../services/wildlife/nps-alerts-service.js'
import type { PlssDescription } from '../../services/reference/plss-service.js'

type TabId = 'details' | 'measure' | 'weather' | 'astro' | 'hazards'

const TAB_KEY = 'cairn_popup_tab'

/** Draw tools offered in the details-tab action band (same set as the radial draw mode). */
const DRAW_TOOLS: Array<{ tool: string; icon: string; label: string; title?: string }> = [
  { tool: 'point', icon: 'location_on', label: 'Point' },
  { tool: 'line', icon: 'show_chart', label: 'Line' },
  { tool: 'polygon', icon: 'hexagon', label: 'Polygon' },
  { tool: 'circle', icon: 'circle', label: 'Circle' },
  { tool: 'rectangle', icon: 'rectangle', label: 'Rect' },
  { tool: 'measure', icon: 'straighten', label: 'Measure', title: 'Measure (ephemeral line)' },
]

const EMERGENCY_META: Record<EmergencyDataType, { label: string; color: string }> = {
  hospitals: { label: 'Hospitals', color: '#dc2626' },
  'fire-stations': { label: 'Fire & EMS', color: '#ea580c' },
  'law-enforcement': { label: 'Law Enforcement', color: '#2563eb' },
  'usfs-offices': { label: 'USFS Offices', color: '#16a34a' },
}

interface EmergencyFeatureHit {
  dataType: EmergencyDataType
  facility: AnyEmergencyFacility
}

interface GlossaryEntry {
  label: string
  description: string
}

const BLM_AGENCIES: Record<string, GlossaryEntry> = {
  BLM: {
    label: 'Bureau of Land Management',
    description:
      'Manages public lands for multiple uses including recreation, grazing, energy, and conservation.',
  },
  FS: {
    label: 'Forest Service',
    description:
      'Manages national forests and grasslands for sustainable use, recreation, and conservation.',
  },
  NPS: {
    label: 'National Park Service',
    description:
      'Preserves natural and cultural resources in national parks, monuments, and historic sites.',
  },
  FWS: {
    label: 'Fish & Wildlife Service',
    description:
      'Manages national wildlife refuges and protects endangered species and their habitats.',
  },
  BIA: {
    label: 'Bureau of Indian Affairs',
    description: 'Holds land in trust for Native American tribes and individuals.',
  },
  BOR: {
    label: 'Bureau of Reclamation',
    description: 'Manages water resources and dams, primarily in western states.',
  },
  DOD: {
    label: 'Department of Defense',
    description: 'Military installations and training areas. Access is typically restricted.',
  },
  DOE: {
    label: 'Department of Energy',
    description: 'Energy research facilities and nuclear reservations. Access is restricted.',
  },
  TVA: {
    label: 'Tennessee Valley Authority',
    description: 'Manages land along the Tennessee River system for flood control and recreation.',
  },
  USACE: {
    label: 'Army Corps of Engineers',
    description: 'Manages waterways, flood control, and recreation areas at reservoirs and dams.',
  },
  PVT: { label: 'Private', description: 'Privately owned land.' },
  ST: { label: 'State', description: 'Land managed by a state agency.' },
}

const GAP_STATUS: Record<string, GlossaryEntry> = {
  '1': {
    label: 'GAP 1 — Managed for Biodiversity',
    description:
      'Permanent protection. Natural disturbance allowed; no extractive uses. Examples: wilderness areas, some national parks.',
  },
  '2': {
    label: 'GAP 2 — Biodiversity, Some Use',
    description:
      'Permanent protection. Managed primarily for biodiversity but some non-extractive activities allowed. Examples: national wildlife refuges, state parks.',
  },
  '3': {
    label: 'GAP 3 — Multiple Use',
    description:
      'Protected but subject to extractive uses like logging, mining, or grazing. Examples: national forests, BLM lands.',
  },
  '4': {
    label: 'GAP 4 — No Protection Mandate',
    description:
      'No legal mandate for conservation. May be managed for other purposes. Examples: Department of Defense lands, utility corridors.',
  },
}

const ACCESS_LEVELS: Record<string, GlossaryEntry> = {
  OA: {
    label: 'Open Access',
    description:
      'No special requirements for public access. May include regular hours of availability.',
  },
  RA: {
    label: 'Restricted Access',
    description:
      'Requires a special permit, registration, or has limited hours. Examples: backcountry wilderness registration, Wild and Scenic River self-permits.',
  },
  XA: {
    label: 'Closed',
    description:
      'Designated as no public access (e.g. military bases, land banks, ecological study areas). Note: some areas that require entry fees or permits are reported as Closed by local agencies — check with the managing authority for current access.',
  },
  UK: {
    label: 'Unknown',
    description: 'Public access status has not been determined for this area.',
  },
}

const DESIGNATION_TYPES: Partial<Record<string, GlossaryEntry>> = {
  // Federal
  ACEC: {
    label: 'Area of Critical Environmental Concern',
    description:
      'BLM-designated area requiring special management to protect important natural, cultural, or scenic values.',
  },
  ACC: {
    label: 'Access Area',
    description: 'Land designated primarily to provide public access to other protected areas.',
  },
  FOTH: {
    label: 'Federal — Other',
    description:
      'Federally managed land that does not fit standard designation categories, or whose designation is unknown.',
  },
  HCA: {
    label: 'Historic or Cultural Area',
    description: 'Area protected for its historic, archaeological, or cultural significance.',
  },
  IRA: {
    label: 'Inventoried Roadless Area',
    description:
      'National Forest land identified as undeveloped and roadless, with restrictions on road building and timber harvest.',
  },
  MIL: {
    label: 'Military Land',
    description:
      'Land managed by the Department of Defense for military installations and training. Access is typically restricted.',
  },
  MIT: {
    label: 'Mitigation Land or Bank',
    description:
      'Land conserved to offset environmental impacts from development or other activities elsewhere.',
  },
  MPA: {
    label: 'Marine Protected Area',
    description: 'Ocean or coastal area managed to protect natural or cultural marine resources.',
  },
  NCA: {
    label: 'National Conservation Area',
    description:
      'BLM-managed area designated by Congress for the conservation of natural, scenic, or cultural values.',
  },
  ND: {
    label: 'Not Designated',
    description: 'Protected land with no specific conservation or management designation.',
  },
  NF: {
    label: 'National Forest',
    description:
      'Forest Service land managed for multiple uses: timber, recreation, wildlife habitat, and watershed protection.',
  },
  NG: {
    label: 'National Grassland',
    description:
      'Forest Service land consisting primarily of grasslands, managed for grazing, wildlife, and recreation.',
  },
  NLS: {
    label: 'National Lakeshore or Seashore',
    description:
      'NPS-managed shoreline area preserved for scenic, recreational, and ecological value.',
  },
  NM: {
    label: 'National Monument or Landmark',
    description:
      'Area designated by the President or Congress to protect objects of historic or scientific interest.',
  },
  NP: {
    label: 'National Park',
    description:
      'NPS-managed area of outstanding natural beauty, unique geology, or ecological significance, preserved for public enjoyment.',
  },
  NRA: {
    label: 'National Recreation Area',
    description:
      'Federally managed area designated for outdoor recreation, often near reservoirs or urban areas.',
  },
  NSBV: {
    label: 'National Scenic, Botanical, or Volcanic Area',
    description:
      'Federally protected area of outstanding scenic beauty, botanical significance, or volcanic features.',
  },
  NT: {
    label: 'National Scenic or Historic Trail',
    description:
      'Long-distance trail designated by Congress for scenic beauty or historical significance (e.g., Appalachian Trail, Pacific Crest Trail).',
  },
  NWR: {
    label: 'National Wildlife Refuge',
    description:
      'FWS-managed land set aside to conserve fish, wildlife, and plants and their habitats.',
  },
  PROC: {
    label: 'Proclamation Boundary',
    description:
      'The approved or proclaimed boundary of a protected area, which may be larger than the currently managed land.',
  },
  PUB: {
    label: 'National Public Lands',
    description:
      'BLM-managed public domain land open to multiple uses including recreation, grazing, and mineral development.',
  },
  REC: {
    label: 'Recreation Management Area',
    description: 'Federally managed land designated primarily for outdoor recreation activities.',
  },
  REA: {
    label: 'Research or Educational Area',
    description: 'Area set aside for scientific research, monitoring, or environmental education.',
  },
  RMA: {
    label: 'Resource Management Area',
    description:
      'Land managed under a specific resource management plan, typically with multiple-use objectives.',
  },
  RNA: {
    label: 'Research Natural Area',
    description:
      'Undisturbed area maintained in its natural condition for ecological research and as a baseline reference.',
  },
  SDA: {
    label: 'Special Designation Area',
    description:
      'Federally designated area with specific management requirements not covered by other categories.',
  },
  TRIBL: {
    label: 'Native American Land',
    description:
      'Land held in trust by the federal government for Native American tribes or individuals.',
  },
  WA: {
    label: 'Wilderness Area',
    description:
      'Congressionally designated area preserved in its natural state. No roads, motorized vehicles, or permanent structures.',
  },
  WPA: {
    label: 'Watershed Protection Area',
    description: 'Land protected to safeguard water quality and supply for downstream communities.',
  },
  WSA: {
    label: 'Wilderness Study Area',
    description:
      'Area under evaluation for potential wilderness designation. Managed to preserve wilderness characteristics.',
  },
  WSR: {
    label: 'Wild and Scenic River',
    description:
      'River segment designated by Congress for its outstanding natural, cultural, or recreational values. Development is restricted.',
  },
  // State
  SHCA: {
    label: 'State Historic or Cultural Area',
    description:
      'State-managed area preserved for its historical, archaeological, or cultural significance.',
  },
  SCON: {
    label: 'State Conservation Area',
    description:
      'State-managed land designated for conservation of natural resources and habitats.',
  },
  SFW: {
    label: 'State Fish and Wildlife Area',
    description:
      'State-managed land for fish and wildlife conservation, habitat management, and hunting or fishing access.',
  },
  SFOR: {
    label: 'State Forest',
    description: 'State-managed forest land for timber production, recreation, and conservation.',
  },
  SOTH: {
    label: 'State — Other',
    description: 'State-managed land that does not fit standard designation categories.',
  },
  SP: {
    label: 'State Park',
    description:
      'State-managed area for public recreation, scenic enjoyment, and resource preservation.',
  },
  SREC: {
    label: 'State Recreation Area',
    description: 'State-managed area designated primarily for outdoor recreation.',
  },
  SRMA: {
    label: 'State Resource Management Area',
    description:
      'State-managed land under a resource management plan with multiple-use objectives.',
  },
  SW: {
    label: 'State Wilderness',
    description:
      'State-designated wilderness area preserved in its natural state, similar to federal wilderness.',
  },
  SCA: {
    label: 'State Conservation Area',
    description:
      'State-managed land designated for conservation of natural resources and habitats.',
  },
  // Local
  LCA: {
    label: 'Local Conservation Area',
    description:
      'Land managed by a city, county, or local government for conservation of natural resources.',
  },
  LCON: {
    label: 'Local Conservation Area',
    description: 'Locally managed land set aside for conservation of natural resources.',
  },
  LHCA: {
    label: 'Local Historic or Cultural Area',
    description:
      'Locally managed area preserved for its historical, archaeological, or cultural significance.',
  },
  LOTH: {
    label: 'Local — Other',
    description:
      'Locally managed land that does not fit standard designation categories, or whose designation is unknown.',
  },
  LP: {
    label: 'Local Park',
    description: 'Park managed by a city, county, or local government for public recreation.',
  },
  LPRA: {
    label: 'Local Protected Area',
    description: 'Locally managed area with formal protection for natural or cultural resources.',
  },
  LREC: {
    label: 'Local Recreation Area',
    description: 'Locally managed area designated primarily for outdoor recreation.',
  },
  LRMA: {
    label: 'Local Resource Management Area',
    description:
      'Locally managed land under a resource management plan with multiple-use objectives.',
  },
  // Private
  PCON: {
    label: 'Private Conservation',
    description:
      'Privately owned land managed for conservation purposes, often by a land trust or conservation organization.',
  },
  PAGR: {
    label: 'Private Agricultural',
    description:
      'Privately owned agricultural land enrolled in a conservation or easement program.',
  },
  PFOR: {
    label: 'Private Forest',
    description: 'Privately owned forest land under a conservation easement or management plan.',
  },
  POTH: {
    label: 'Private — Other',
    description:
      'Privately owned land in a conservation program that does not fit standard categories.',
  },
  PPRK: {
    label: 'Private Park',
    description: 'Privately owned park or preserve managed for public or member recreation.',
  },
  PHCA: {
    label: 'Private Historic or Cultural',
    description:
      'Privately owned land preserved for its historical, archaeological, or cultural significance.',
  },
  PRAN: {
    label: 'Private Ranch',
    description: 'Privately owned ranch land under a conservation easement or management plan.',
  },
  PREC: {
    label: 'Private Recreation',
    description:
      'Privately owned land managed for recreational use under a conservation agreement.',
  },
  // Easements
  CONE: {
    label: 'Conservation Easement',
    description:
      'A legal agreement that permanently limits uses of land to protect its conservation values.',
  },
  RECE: {
    label: 'Recreation or Education Easement',
    description: 'An easement protecting land for recreational or educational purposes.',
  },
  HCAE: {
    label: 'Historic or Cultural Easement',
    description: 'An easement protecting land for its historical or cultural significance.',
  },
  AGRE: {
    label: 'Agricultural Easement',
    description: 'An easement preserving agricultural use of the land and preventing development.',
  },
  RANE: {
    label: 'Ranch Easement',
    description: 'An easement preserving ranch land and open space from development.',
  },
  FORE: {
    label: 'Forest Stewardship Easement',
    description: 'An easement protecting forest land for sustainable forestry and conservation.',
  },
  OTHE: {
    label: 'Other Easement',
    description: 'A conservation easement that does not fit standard easement categories.',
  },
  UNKE: {
    label: 'Unknown Easement',
    description: 'An easement whose specific type has not been determined.',
  },
  // Other
  OCS: {
    label: 'Outer Continental Shelf',
    description: 'Federal submerged lands on the outer continental shelf, beyond state waters.',
  },
  FACY: {
    label: 'Facility',
    description: 'A building, structure, or developed site within a protected area.',
  },
  UNK: {
    label: 'Unknown Designation',
    description: 'The designation type for this area has not been determined.',
  },
}

interface AstroEvent extends GlossaryEntry {
  color: string
}

const ASTRO_EVENTS: Record<string, AstroEvent> = {
  nightEnd: {
    label: 'Astronomical Dawn',
    description: 'Sun is 18° below the horizon. Stars begin to fade as the faintest light appears.',
    color: '#1e293b',
  },
  nauticalDawn: {
    label: 'Nautical Dawn',
    description:
      'Sun is 12° below the horizon. The horizon becomes visible at sea and general outlines of objects appear.',
    color: '#334155',
  },
  dawn: {
    label: 'Civil Dawn',
    description:
      'Sun is 6° below the horizon. Enough natural light for most outdoor activities without artificial lighting.',
    color: '#ea580c',
  },
  sunrise: {
    label: 'Sunrise',
    description: 'The upper edge of the sun appears on the horizon, marking the start of daylight.',
    color: '#f59e0b',
  },
  solarNoon: {
    label: 'Solar Noon',
    description: 'The sun reaches its highest point in the sky. Shadows are at their shortest.',
    color: '#38bdf8',
  },
  sunset: {
    label: 'Sunset',
    description:
      'The upper edge of the sun disappears below the horizon, marking the end of daylight.',
    color: '#f59e0b',
  },
  dusk: {
    label: 'Civil Dusk',
    description:
      'Sun is 6° below the horizon. Last period with enough natural light for outdoor activities.',
    color: '#ea580c',
  },
  nauticalDusk: {
    label: 'Nautical Dusk',
    description:
      'Sun is 12° below the horizon. The horizon is no longer visible at sea. Navigation stars appear.',
    color: '#334155',
  },
  night: {
    label: 'Astronomical Dusk',
    description:
      'Sun is 18° below the horizon. Full astronomical darkness begins and all stars are visible.',
    color: '#1e293b',
  },
  moonrise: {
    label: 'Moonrise',
    description: 'The upper edge of the moon appears above the horizon.',
    color: '#64748b',
  },
  moonset: {
    label: 'Moonset',
    description: 'The upper edge of the moon disappears below the horizon.',
    color: '#64748b',
  },
}

interface LandFeatureInfo {
  layerType: 'padus' | 'blm'
  name?: string | undefined
  desCode?: string | undefined
  manager?: string | undefined
  accessCode?: string | undefined
  gapCode?: string | undefined
  agency?: string | undefined
  unit?: string | undefined
  stateCode?: string | undefined
  area?: string | undefined
}

interface UserFeatureInfo {
  id: string
  name: string
  type: string
  geometry: GeoJSON.Geometry
  association?: { kind: 'activity' | 'plan'; id: string; name: string }
  elevation?: number
}

/** Material icon for a drawn feature's geometry type. */
function userFeatureIcon(type: string): string {
  switch (type) {
    case 'point':
      return 'place'
    case 'line':
      return 'polyline'
    case 'route':
    case 'track':
      return 'route'
    case 'polygon':
      return 'pentagon'
    case 'circle':
      return 'circle'
    case 'rectangle':
      return 'crop_square'
    default:
      return 'place'
  }
}

export interface ActivityTrackInfo {
  distanceM: number
  elevationGainM: number
  elevationLossM: number
  elevationM: number | null
  elapsedS: number
  rest: { duration: number; radius: number } | null
}

@customElement('map-context-popup')
export class MapContextPopup extends LitElement {
  static override styles = css`
    :host {
      display: block;
      font-family: var(--cairn-font-family, system-ui, sans-serif);
      color: var(--cairn-text-color, #1e293b);
      font-size: 13px;
      line-height: 1.4;
      min-width: 280px;
      max-width: 400px;
    }

    details summary {
      cursor: pointer;
    }

    details summary::-webkit-details-marker {
      display: none;
    }

    details summary::marker {
      content: '';
    }

    .avy-popover-trigger {
      cursor: pointer;
      text-decoration: underline;
      text-decoration-style: dotted;
      text-underline-offset: 2px;
      font-weight: 600;
    }

    .avy-popover-trigger:hover {
      text-decoration-style: solid;
    }

    .avy-popover-overlay {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.3);
    }

    .avy-popover {
      position: relative;
      background: var(--cairn-surface-color, #fff);
      color: var(--cairn-text-color, #1e293b);
      border-radius: 10px;
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
      padding: 16px;
      max-width: 360px;
    }

    .avy-popover-close {
      position: absolute;
      top: 4px;
      right: 8px;
      background: none;
      border: none;
      font-size: 1.2rem;
      cursor: pointer;
      color: var(--cairn-text-secondary-color, #64748b);
      line-height: 1;
    }

    .avy-details {
      margin-top: 4px;
    }

    .avy-details-summary {
      display: flex;
      align-items: center;
      gap: 2px;
      cursor: pointer;
      color: var(--cairn-text-secondary-color, #64748b);
      list-style: none;
    }

    .avy-details-summary::-webkit-details-marker {
      display: none;
    }

    .avy-details-summary > span:first-child {
      text-decoration: underline;
      text-decoration-style: dotted;
      text-underline-offset: 2px;
    }

    .avy-details-summary:hover {
      color: var(--cairn-text-color, #1e293b);
    }

    .avy-details-summary:hover > span:first-child {
      text-decoration-style: solid;
    }

    .avy-details-caret {
      font-size: 14px;
      transition: transform 0.2s;
    }

    .avy-details[open] .avy-details-caret {
      transform: rotate(90deg);
    }

    .popup-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      margin-bottom: 4px;
    }

    .tab-bar {
      display: flex;
      gap: 0;
      margin-bottom: -1px;
    }

    .tab-button {
      background: none;
      border: none;
      padding: 6px 10px;
      cursor: pointer;
      color: var(--cairn-text-muted-color, #94a3b8);
      border-bottom: 2px solid transparent;
      transition:
        color 0.15s,
        border-color 0.15s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .tab-button:hover {
      color: var(--cairn-text-color, #1e293b);
    }

    .tab-button.active {
      color: var(--cairn-primary-color, #3b82f6);
      border-bottom-color: var(--cairn-primary-color, #3b82f6);
    }

    .tab-button .material-icons {
      font-size: 20px !important;
    }

    :host([fullscreen]) .tab-button {
      padding: 10px 16px;
    }

    :host([fullscreen]) .tab-button .material-icons {
      font-size: 24px !important;
    }

    :host([fullscreen]) .close-button {
      display: none;
    }

    .close-button {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--cairn-text-muted-color, #94a3b8);
      padding: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition:
        background 0.15s,
        color 0.15s;
    }

    .close-button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-color, #1e293b);
    }

    .close-button .material-icons {
      font-size: 22px !important;
    }

    .tab-content {
      padding: 8px 0 4px 0;
      min-height: 80px;
    }

    .loading {
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--cairn-text-muted-color, #94a3b8);
      padding: 12px 0;
    }

    .spinner {
      width: 16px;
      height: 16px;
      border: 2px solid var(--cairn-border-color, #e2e8f0);
      border-top-color: var(--cairn-primary-color, #3b82f6);
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .section-label {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--cairn-text-muted-color, #94a3b8);
      margin: 8px 0 4px 0;
    }

    .section-label:first-child {
      margin-top: 0;
    }

    .detail-row {
      display: flex;
      justify-content: space-between;
      padding: 2px 0;
    }

    .detail-label {
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .detail-value {
      font-weight: 500;
      text-align: right;
    }

    .location-name {
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 2px;
    }

    .feature-type {
      font-weight: 400;
      color: var(--cairn-text-muted-color, #94a3b8);
      font-size: 12px;
    }

    .location-address {
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 12px;
      margin-bottom: 6px;
    }

    .land-feature {
      padding: 6px 0;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .land-feature:last-child {
      border-bottom: none;
    }

    .land-feature-name {
      font-weight: 600;
    }

    .land-feature-detail {
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 12px;
    }

    .emergency-facility {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 4px 0;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .emergency-facility:last-child {
      border-bottom: none;
    }

    .emergency-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
      margin-top: 4px;
    }

    .emergency-facility-info {
      flex: 1;
      min-width: 0;
    }

    .emergency-facility-name {
      font-weight: 500;
      font-size: 12px;
    }

    .emergency-facility-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 11px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .emergency-facility-meta a {
      color: var(--cairn-primary-color, #3b82f6);
      text-decoration: none;
    }

    .emergency-facility-meta a:hover {
      text-decoration: underline;
    }

    .user-feature {
      padding: 4px 0;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .user-feature:last-child {
      border-bottom: none;
    }

    .user-feature-name {
      font-weight: 500;
    }

    .user-feature-main {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      min-width: 0;
    }

    .user-feature-text {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    .user-feature-icon {
      flex-shrink: 0;
      font-size: 18px !important;
      color: var(--cairn-text-secondary-color, #64748b);
      margin-top: 1px;
    }

    .user-feature-subtitle {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 1px;
    }

    .user-feature-measurement {
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 12px;
    }

    .user-feature-sep {
      color: var(--cairn-text-muted-color, #94a3b8);
      font-size: 12px;
    }

    .user-feature-link {
      padding: 0;
      background: transparent;
      border: none;
      cursor: pointer;
      font-size: 12px;
      font-family: inherit;
      color: var(--cairn-primary-color, #3b82f6);
    }

    .user-feature-link:hover {
      text-decoration: underline;
    }

    .user-feature-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 8px;
    }

    .user-feature-actions {
      display: flex;
      align-items: center;
      gap: 2px;
      flex-shrink: 0;
    }

    .user-feature-edit {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      padding: 0;
      color: var(--cairn-text-color, #1e293b);
      background: transparent;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    }

    .user-feature-edit .material-icons {
      font-size: 18px;
    }

    .user-feature-edit:hover {
      background: var(--cairn-hover-color, rgba(0, 0, 0, 0.06));
    }

    /* Measure verbs band (Measure tab). */
    .map-actions {
      margin-bottom: 8px;
      padding-bottom: 8px;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    /* Details header: location block (name / city,state / county) on the left,
       draw toolbar top-right. The toolbar renders regardless of geocode state. */
    .details-header {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      margin-bottom: 4px;
    }

    .details-location {
      flex: 1;
      min-width: 0;
    }

    .details-header .action-draw-tools {
      flex-shrink: 0;
      margin-left: auto;
    }

    .action-draw-tools {
      display: flex;
      align-items: center;
      gap: 2px;
    }

    .action-tool {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 6px 9px;
      background: transparent;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      color: var(--cairn-text-secondary-color, #94a3b8);
      font-family: inherit;
    }

    .action-tool:hover {
      color: var(--cairn-text-color, #1e293b);
      background: var(--cairn-hover-color, rgba(0, 0, 0, 0.06));
    }

    .action-tool .material-icons {
      font-size: 20px !important;
    }

    .action-verbs {
      display: flex;
      flex-direction: column;
      margin-top: 2px;
    }

    .action-verb {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 8px;
      background: transparent;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 13px;
      font-family: inherit;
      color: var(--cairn-text-color, #1e293b);
      text-align: left;
    }

    .action-verb:hover {
      background: var(--cairn-hover-color, rgba(0, 0, 0, 0.06));
    }

    .action-verb .material-icons {
      font-size: 18px !important;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-main {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 8px;
    }

    .weather-icon {
      font-size: 32px !important;
      color: var(--cairn-primary-color, #3b82f6);
    }

    .weather-temp {
      font-size: 24px;
      font-weight: 600;
    }

    .weather-feels {
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-condition {
      font-weight: 500;
      margin-bottom: 6px;
    }

    .weather-details {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }

    .weather-detail {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-detail .glossary-def {
      flex-basis: 100%;
    }

    .weather-detail .material-icons {
      font-size: 16px !important;
    }

    .popup-forecast-label {
      font-size: 11px;
      font-weight: 500;
      color: var(--cairn-text-secondary-color, #64748b);
      margin-top: 10px;
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .popup-forecast-blocks {
      display: flex;
      gap: 2px;
      overflow-x: auto;
      padding-bottom: 4px;
    }

    .popup-forecast-block {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      min-width: 44px;
      padding: 4px 3px;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.03);
      font-size: 11px;
    }

    .popup-block-time {
      font-weight: 600;
      font-size: 10px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .popup-block-icon {
      font-size: 16px;
      color: var(--cairn-primary-color, #3b82f6);
    }

    .popup-block-temp {
      font-weight: 600;
      font-size: 11px;
    }

    .popup-block-wind {
      display: flex;
      align-items: center;
      gap: 1px;
      font-size: 10px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .popup-block-wind .material-icons {
      font-size: 11px !important;
    }

    .popup-block-lightning {
      font-size: 13px;
    }

    .weather-snow {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .weather-snow-header {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 13px;
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin-bottom: 4px;
    }

    .astro-diagram {
      padding: 4px 0;
    }

    .astro-diagram svg {
      width: 100%;
      display: block;
    }

    .arc-label {
      font-family: var(--cairn-font-family, system-ui, sans-serif);
      font-size: 9px;
      fill: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      text-decoration-line: underline;
      text-decoration-style: dotted;
      text-decoration-color: var(--cairn-text-muted-color, #94a3b8);
    }

    .arc-label:hover {
      fill: var(--cairn-primary-color, #3b82f6);
    }

    .arc-label.active {
      font-weight: 700;
      fill: var(--cairn-text-color, #1e293b);
    }

    .arc-def {
      font-size: 11px;
      color: var(--cairn-text-secondary-color, #64748b);
      line-height: 1.35;
      padding: 4px 8px;
      animation: fadeIn 0.15s ease-out;
    }

    .arc-def strong {
      color: var(--cairn-text-color, #1e293b);
    }

    .moon-phase-label {
      text-align: center;
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
      padding: 2px 0;
    }

    .astro-separator {
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
      margin: 6px 0;
    }

    .glossary-term {
      text-decoration: underline dotted var(--cairn-text-muted-color, #94a3b8);
      text-underline-offset: 2px;
      cursor: pointer;
      transition: color 0.15s;
    }

    .glossary-term:hover {
      color: var(--cairn-primary-color, #3b82f6);
    }

    .glossary-def {
      font-size: 11px;
      color: var(--cairn-text-secondary-color, #64748b);
      line-height: 1.35;
      padding: 3px 0 2px 0;
      animation: fadeIn 0.15s ease-out;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .compact-item {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 0;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .compact-item:last-child {
      border-bottom: none;
    }

    .compact-item .material-icons,
    .compact-item > img {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      line-height: 20px;
      text-align: center;
      object-fit: contain;
    }

    .compact-body {
      flex: 1;
      min-width: 0;
    }

    .compact-title-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 8px;
    }

    .compact-title {
      font-weight: 600;
      font-size: 13px;
    }

    .compact-right {
      font-size: 11px;
      color: var(--cairn-text-secondary-color, #64748b);
      white-space: nowrap;
      flex-shrink: 0;
    }

    .compact-sub {
      font-size: 11px;
      color: var(--cairn-text-secondary-color, #64748b);
      margin-top: 1px;
    }

    .activity-icons {
      display: inline-flex;
      gap: 6px;
      align-items: center;
    }

    .activity-icons .material-icons {
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .activity-icons svg {
      width: 24px;
      height: 24px;
      fill: var(--cairn-text-secondary-color, #64748b);
      vertical-align: middle;
    }

    .icon-badge {
      position: relative;
      display: inline-flex;
    }

    .icon-badge .badge {
      position: absolute;
      bottom: -2px;
      right: -4px;
      font-size: 14px !important;
      background: white;
      border-radius: 50%;
    }

    .no-data {
      color: var(--cairn-text-muted-color, #94a3b8);
      font-style: italic;
      padding: 8px 0;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      font-size: 24px !important;
      line-height: 1 !important;
      letter-spacing: normal !important;
      text-transform: none !important;
      display: inline-block !important;
      white-space: nowrap !important;
      word-wrap: normal !important;
      direction: ltr !important;
      user-select: none !important;
    }

    @keyframes lightbox-in {
      from {
        opacity: 0;
        transform: scale(0.85);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }

    .photo-lightbox {
      position: fixed;
      inset: 0;
      z-index: 9999;
      background: rgba(0, 0, 0, 0.85);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      animation: lightbox-in 0.2s ease-out;
    }

    .photo-lightbox-img {
      max-width: 90vw;
      max-height: 90vh;
      object-fit: contain;
      border-radius: 8px;
      border: 3px solid var(--cairn-border-color, #e2e8f0);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      animation: lightbox-in 0.25s ease-out;
    }

    .gallery-nav {
      position: fixed;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(0, 0, 0, 0.6);
      color: #fff;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10000;
    }
    .gallery-nav:hover {
      background: rgba(0, 0, 0, 0.8);
    }
    .gallery-prev {
      left: 12px;
    }
    .gallery-next {
      right: 12px;
    }
    .gallery-counter {
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      color: #fff;
      font-size: 0.85rem;
      background: rgba(0, 0, 0, 0.5);
      padding: 4px 12px;
      border-radius: 12px;
      z-index: 10000;
    }

    .crag-detail {
      padding: 0 0 4px 26px;
    }
    .crag-photos {
      display: flex;
      gap: 4px;
      padding: 4px 0;
      overflow-x: auto;
    }
    .crag-thumb {
      width: 48px;
      height: 48px;
      border-radius: 4px;
      object-fit: cover;
      cursor: pointer;
      flex-shrink: 0;
    }
    .crag-thumb:hover {
      opacity: 0.8;
    }
    .climb-list-label {
      font-size: 0.7rem;
      color: var(--cairn-text-secondary-color, #64748b);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 6px 0 2px;
    }
    .climb-row {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 2px 0;
      font-size: 0.8rem;
    }
    .climb-name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .climb-grade {
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      white-space: nowrap;
    }
    .climb-type {
      font-size: 0.7rem;
      color: var(--cairn-text-secondary-color, #64748b);
      white-space: nowrap;
    }
    .climb-length {
      font-size: 0.7rem;
      color: var(--cairn-text-secondary-color, #64748b);
      white-space: nowrap;
    }
    .climb-thumb {
      width: 28px;
      height: 28px;
      border-radius: 3px;
      object-fit: cover;
      cursor: pointer;
      flex-shrink: 0;
    }
    .climb-more {
      font-size: 0.75rem;
      color: var(--cairn-text-secondary-color, #64748b);
      padding: 2px 0;
      font-style: italic;
    }
  `

  @property({ type: Boolean, reflect: true }) fullscreen = false
  @property({ type: Number }) lat = 0
  @property({ type: Number }) lng = 0
  @property({ attribute: false }) landFeatures: maplibregl.MapGeoJSONFeature[] = []
  @property({ attribute: false }) plss: PlssDescription | null = null
  @property({ attribute: false }) backcountryFeatures: maplibregl.MapGeoJSONFeature[] = []
  @property({ attribute: false }) userFeatures: UserFeatureInfo[] = []
  @property({ attribute: false }) emergencyFeatures: EmergencyFeatureHit[] = []
  @property({ attribute: false }) inatObservations: INatObservation[] = []
  @property({ attribute: false }) criticalHabitatFeatures: maplibregl.MapGeoJSONFeature[] = []
  @property({ attribute: false }) cotrexFeatures: maplibregl.MapGeoJSONFeature[] = []
  @property({ attribute: false }) closureFeatures: maplibregl.MapGeoJSONFeature[] = []
  @property({ attribute: false }) cellularFeatures: maplibregl.MapGeoJSONFeature[] = []
  @property({ attribute: false }) fireFeatures: Array<{
    fireType: string
    feature: maplibregl.MapGeoJSONFeature
  }> = []
  @property({ attribute: false }) avalancheZoneFeatures: maplibregl.MapGeoJSONFeature[] = []
  @property({ attribute: false }) npsAlerts: NPSAlert[] = []
  @property({ attribute: false }) clickedCrags: Array<{
    uuid: string
    name: string
    totalClimbs: number
    disciplines: Record<string, number>
  }> = []
  @property({ attribute: false }) climbFilter: ClimbFilterOptions | null = null
  @property({ attribute: false }) activityTrackInfo: ActivityTrackInfo | null = null

  @state() private activeTab: TabId = 'details'
  @state() private geocodeResult: ReverseGeocodeResult | null = null
  @state() private geocodeLoading = true
  @state() private weather: CurrentConditions | null = null
  @state() private weatherData: WeatherData | null = null
  @state() private weatherLoading = false
  @state() private snowData: {
    snodasDepthMm: number | null
    ndsi: number | null
  } | null = null
  @state() private elevation: number | null = null
  @state() private elevationLoading = false
  @state() private expandedTerms = new Set<string>()
  @state() private activeAstroKey: string | null = null
  @state() private expandedPhotoUrl: string | null = null
  @state() private galleryPhotos: string[] = []
  @state() private galleryIndex = 0
  @state() private speciesPhotos = new Map<string, string | null>()
  @state() private nearbyCrags: Array<{
    u: string
    n: string
    c: [number, number]
    t: number
    p: string[]
    d?: { s?: number; r?: number; b?: number; a?: number; t?: number }
    l?: boolean
  }> = []
  @state() private climbingLoading = false
  @state() private expandedCragUuid: string | null = null
  @state() private cragDetails = new Map<string, OpenBetaAreaDetail>()
  @state() private cragDetailsLoading = new Set<string>()
  @state() private cragDetailsFailed = new Set<string>()
  @state() private cragDrillStack: Array<{ uuid: string; name: string }> = []
  @state() private cragDrillCurrent: { uuid: string; name: string } | null = null

  @state() private hazardsLoading = false
  @state() private avalancheZone: AvalancheZoneProperties | null = null
  @state() private avyPopoverKey: string | null = null
  @state() private hazardFireFeatures: FireFeatureHit[] = []
  @state() private hazardNpsAlerts: (NPSAlert & { distanceKm: number })[] = []

  private settingsStorage = new SettingsStorage()
  private weatherService = new WeatherService()

  private get settings(): ReturnType<SettingsStorage['getSettingsWithDefaults']> {
    return this.settingsStorage.getSettingsWithDefaults()
  }

  private get units(): UnitSystem {
    return this.settings.unitSystem as UnitSystem
  }

  override connectedCallback(): void {
    super.connectedCallback()
    const saved = sessionStorage.getItem(TAB_KEY) as TabId | null
    if (saved && ['details', 'measure', 'weather', 'astro', 'hazards'].includes(saved)) {
      this.activeTab = saved
    }
    void this.loadGeocodeData()
    if (this.activeTab === 'weather') void this.loadWeatherData()
    if (this.activeTab === 'measure') void this.loadElevation()
    if (this.activeTab === 'hazards') void this.loadHazardsData()
    void this.loadSpeciesPhotos()
    void this.loadNearbyCrags()
  }

  private async loadGeocodeData(): Promise<void> {
    this.geocodeLoading = true
    this.geocodeResult = await reverseGeocodeService.lookup(this.lat, this.lng)
    this.geocodeLoading = false
  }

  private async loadWeatherData(): Promise<void> {
    if (this.weather) return
    this.weatherLoading = true
    try {
      const [weatherResult, snodasDepth, ndsi] = await Promise.all([
        this.weatherService.getWeather(this.lat, this.lng, { includeEnsemble: true }),
        snowDepthService.queryDepthAtPoint(this.lat, this.lng).catch(() => null),
        this.queryNdsiAtPoint(this.lat, this.lng).catch(() => null),
      ])
      this.weather = weatherResult?.data.current ?? null
      this.weatherData = weatherResult?.data ?? null
      this.snowData = { snodasDepthMm: snodasDepth, ndsi }
    } catch {
      // Weather fetch failed
    }
    this.weatherLoading = false
  }

  private async loadHazardsData(): Promise<void> {
    if (this.avalancheZone) return
    this.hazardsLoading = true
    try {
      await avalancheLayerService.ensureDate(avalancheLayerService.date)
      const zone = avalancheLayerService.resolveZone(this.lat, this.lng)
      this.avalancheZone = zone

      const [fire, nps, weatherResult] = await Promise.all([
        fireLayerService.queryNearPoint(this.lat, this.lng).catch(() => [] as FireFeatureHit[]),
        npsAlertsService
          .queryNearPoint(this.lat, this.lng)
          .catch(() => [] as (NPSAlert & { distanceKm: number })[]),
        this.weatherData
          ? Promise.resolve(null)
          : this.weatherService.getWeather(this.lat, this.lng).catch(() => null),
      ])
      this.hazardFireFeatures = fire
      this.hazardNpsAlerts = nps
      if (!this.weatherData && weatherResult) {
        this.weather = weatherResult.data.current
        this.weatherData = weatherResult.data
      }
    } catch {
      // Hazards fetch failed
    }
    this.hazardsLoading = false
  }

  private async loadElevation(): Promise<void> {
    if (this.elevation !== null) return
    this.elevationLoading = true
    this.elevation = await elevationPointService.getElevation(this.lat, this.lng)
    this.elevationLoading = false
  }

  private async loadSpeciesPhotos(): Promise<void> {
    for (const f of this.criticalHabitatFeatures) {
      const sciname = (f.properties.sciname as string) || ''
      if (!sciname || this.speciesPhotos.has(sciname)) continue
      this.speciesPhotos.set(sciname, null)
      try {
        const resp = await fetch(
          `https://api.inaturalist.org/v1/taxa/autocomplete?q=${encodeURIComponent(sciname)}&per_page=1&is_active=true`
        )
        if (!resp.ok) continue
        const data = await resp.json()
        const results = data.results as Array<Record<string, unknown>>
        if (results.length > 0) {
          const photo = results[0].default_photo as Record<string, string> | undefined
          if (photo?.square_url) {
            this.speciesPhotos = new Map(this.speciesPhotos).set(sciname, photo.square_url)
          }
        }
      } catch {
        // iNaturalist lookup failed — leave as null
      }
    }
  }

  private setTab(tab: TabId): void {
    this.activeTab = tab
    sessionStorage.setItem(TAB_KEY, tab)
    if (tab === 'weather') void this.loadWeatherData()
    if (tab === 'measure') void this.loadElevation()
    if (tab === 'hazards') void this.loadHazardsData()
  }

  private handleClose(): void {
    this.dispatchEvent(new CustomEvent('close-popup', { bubbles: true, composed: true }))
  }

  private handleEditFeature(featureId: string): void {
    this.dispatchEvent(
      new CustomEvent('edit-feature', {
        bubbles: true,
        composed: true,
        detail: { featureId },
      })
    )
    this.handleClose()
  }

  private handleViewFeature(featureId: string): void {
    this.dispatchEvent(
      new CustomEvent('view-feature', {
        bubbles: true,
        composed: true,
        detail: { featureId },
      })
    )
    this.handleClose()
  }

  // ── Temperature/wind conversions (weather API returns °F and mph) ──

  private tempVal(f: number): number {
    return this.units === 'metric' ? Math.round(((f - 32) * 5) / 9) : Math.round(f)
  }

  private get tempUnit(): string {
    return this.units === 'metric' ? '°C' : '°F'
  }

  private windVal(mph: number): number {
    return this.units === 'metric' ? Math.round(mph * 1.60934) : Math.round(mph)
  }

  private get windUnit(): string {
    return this.units === 'metric' ? 'km/h' : 'mph'
  }

  // ── Glossary terms ──

  private toggleTerm(key: string): void {
    const next = new Set(this.expandedTerms)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    this.expandedTerms = next
  }

  private renderGlossaryTerm(key: string, entry: GlossaryEntry): unknown {
    const expanded = this.expandedTerms.has(key)
    return html`
      <span>
        <span class="glossary-term" @click=${() => this.toggleTerm(key)}>${entry.label}</span>
        ${expanded ? html`<div class="glossary-def">${entry.description}</div>` : nothing}
      </span>
    `
  }

  // ── Land feature parsing ──

  private computeArea(geometry: GeoJSON.Geometry): string | undefined {
    if (geometry.type === 'Polygon') {
      return convertArea(area(polygon(geometry.coordinates)), this.units)
    }
    if (geometry.type === 'MultiPolygon') {
      return convertArea(area(multiPolygon(geometry.coordinates)), this.units)
    }
    return undefined
  }

  private resolveManagerName(rawName: string | undefined): string | undefined {
    if (!rawName) return undefined
    const geo = this.geocodeResult
    if (rawName === 'CITY') return geo?.city ? `City of ${geo.city}` : 'City Government'
    if (rawName === 'CNTY') return geo?.county ? `${geo.county} County` : 'County Government'
    if (rawName === 'JNT') return 'Jointly Managed'
    if (rawName === 'NGO') return 'Non-Governmental Organization'
    if (rawName === 'PVT') return 'Private'
    if (rawName === 'UNK') return 'Unknown'
    if (rawName === 'DESG') return 'Designation Manager'
    return rawName
  }

  private parseLandFeatures(): LandFeatureInfo[] {
    return this.landFeatures.map(f => {
      const p = f.properties
      const area = this.computeArea(f.geometry)
      if (f.layer.id === 'padus-fill') {
        return {
          layerType: 'padus' as const,
          name: p.Unit_Nm ?? undefined,
          desCode: p.Des_Tp ?? undefined,
          manager: this.resolveManagerName(p.Mang_Name),
          accessCode: p.Pub_Access ?? undefined,
          gapCode: p.GAP_Sts ? String(p.GAP_Sts) : undefined,
          area,
        }
      }
      return {
        layerType: 'blm' as const,
        agency: p.ADMIN_AGENCY_CODE ?? undefined,
        unit: p.ADMIN_UNIT_NAME ?? undefined,
        stateCode: p.ADMIN_ST ?? undefined,
        area,
      }
    })
  }

  /** Length of a line geometry in the user's units, or '' if not a line. */
  private lineLength(geom: GeoJSON.Geometry): string {
    if (geom.type === 'LineString') {
      return convertDistance(
        turfLength(lineString(geom.coordinates), { units: 'meters' }),
        this.units
      )
    }
    if (geom.type === 'MultiLineString') {
      return convertDistance(
        turfLength(multiLineString(geom.coordinates), { units: 'meters' }),
        this.units
      )
    }
    return ''
  }

  private renderBackcountrySection(): unknown {
    const seen = new Set<string>()
    const items: Array<{ icon: string; color: string; title: string; subtitle: string }> = []

    for (const f of this.backcountryFeatures) {
      const p = f.properties
      const layerId = f.layer.id
      const key = `${layerId}:${p.name ?? ''}:${p.ele ?? ''}:${p.subclass ?? ''}`
      if (seen.has(key)) continue
      seen.add(key)

      if (layerId.includes('trail-label') || layerId === 'backcountry-trails') {
        const name = p.name ?? p.ref ?? 'Unnamed trail'
        items.push({
          icon: 'hiking',
          color: '#E91E63',
          title: name,
          subtitle: this.lineLength(f.geometry),
        })
      } else if (layerId.includes('pass')) {
        const name = p.name ?? 'Unnamed pass'
        const eleM = p.ele ? Number(p.ele) : null
        const elev = eleM !== null ? convertElevation(eleM, this.units) : ''
        items.push({ icon: 'swap_vert', color: '#7C3AED', title: name, subtitle: elev })
      } else if (layerId.includes('peak')) {
        const name = p.name ?? 'Unnamed peak'
        const eleM = p.ele ? Number(p.ele) : null
        const elev = eleM !== null ? convertElevation(eleM, this.units) : ''
        items.push({ icon: 'landscape', color: '#FF6F00', title: name, subtitle: elev })
      } else if (layerId.includes('shelter')) {
        const name = p.name ?? 'Shelter'
        items.push({ icon: 'cabin', color: '#4CAF50', title: name, subtitle: '' })
      } else if (layerId.includes('campsite')) {
        const name = p.name ?? 'Campsite'
        items.push({ icon: 'camping', color: '#795548', title: name, subtitle: '' })
      } else if (layerId.includes('waterfall')) {
        const name = p.name ?? 'Waterfall'
        items.push({ icon: 'waves', color: '#0891B2', title: name, subtitle: '' })
      } else if (layerId.includes('water')) {
        const name = p.name ?? 'Water source'
        items.push({ icon: 'water_drop', color: '#2196F3', title: name, subtitle: '' })
      } else if (layerId.includes('trailhead')) {
        const name = p.name ?? (p.subclass === 'parking' ? 'Parking' : 'Trailhead')
        items.push({ icon: 'flag', color: '#0D9488', title: name, subtitle: '' })
      } else if (layerId.includes('viewpoint')) {
        const name = p.name ?? 'Viewpoint'
        items.push({ icon: 'visibility', color: '#0284C7', title: name, subtitle: '' })
      } else if (layerId.includes('facilit')) {
        const raw = p.name ?? p.subclass?.replace(/_/g, ' ') ?? 'Facility'
        const label = raw.charAt(0).toUpperCase() + raw.slice(1)
        items.push({ icon: 'info', color: '#6B7280', title: label, subtitle: '' })
      }
    }

    if (items.length === 0) return nothing

    return html`${items.map(
      item => html`
        <div class="compact-item">
          <span class="material-icons" style="color:${item.color};font-size:18px"
            >${item.icon}</span
          >
          <div class="compact-body">
            <div class="compact-title">${item.title}</div>
            ${item.subtitle ? html`<div class="compact-sub">${item.subtitle}</div>` : nothing}
          </div>
        </div>
      `
    )}`
  }

  private renderActivityIcon(field: string): unknown {
    const ACTIVITY_ICONS: Record<string, string> = {
      hiking: 'hiking',
      bike: 'directions_bike',
      motorcycle: 'two_wheeler',
      atv: 'sports_motorsports',
      ohv_gt_50: 'sports_motorsports',
      snowmobile: 'snowmobile',
      ski: 'downhill_skiing',
      snowshoe: 'snowshoeing',
    }
    const ACTIVITY_LABELS: Record<string, string> = {
      hiking: 'Hiking',
      bike: 'Biking',
      horse: 'Horseback riding',
      motorcycle: 'Motorcycle',
      atv: 'ATV',
      ohv_gt_50: 'OHV (>50")',
      snowmobile: 'Snowmobile',
      ski: 'Skiing',
      snowshoe: 'Snowshoeing',
    }
    const label = ACTIVITY_LABELS[field] ?? field.replace(/_/g, ' ')
    if (field === 'horse') {
      return html`<span title=${label}
        ><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 1200">
          <g transform="scale(-1,1) translate(-1200,0)">
            <path
              d="m629.61 186.61c-29.949 0-54.363-24.434-54.363-54.367 0-29.969 24.414-54.367 54.363-54.367 29.953 0 54.387 24.398 54.387 54.367 0 29.934-24.434 54.367-54.387 54.367zm-389.81 285.95c-1.582-4.0352-1.0312-2.9688-2.6016-6.9844l-9.0664-27.434-2.3828-3.8984-3.8828 1.75-11.648 3.8828-15.133 1.9336-24.402-2.3672-6.6992 2.8164-9.082 1.4844-30.453 7.582-5.6133 8.1992 8.4336 11.668 16.25 11.266 30.465 5.832c25.035 2.168 46.434-4.332 65.816-15.73zm7.3164 14.449c-13.266 15.184-34.848 29.051-65.348 21.367-12.336-1.9336-21.402-7.1172-33.719-9.0664l-27.234-13.617-9.7656-17.086-6.4648 1.9336-3.668-0.41406-6.6992-1.9688-9.5195-4.9648-15.766-4.9492-1.7148-5.6172-4.5508-0.44922-3.668-6.6836-1.0664-11.02v-5.6172l1.7188-8.8477 0.63281-9.7188 3.0312-8.4336 9.3008-5.832 11.434-5.3984 36.949-33.484 17.27-17.934 0.64844-8.8516 4.75-5.1797 10.168-5.4023 24.832-17.699 18.367-12.102-1.9492-3.4648-12.734-18.367-5.4023-10.785v-11.25l3.4531-1.7148 9.0664 7.332 9.5156 9.3008 12.715 5.4023-3.8633-12.969-1.7344-12.734 3.4648-5.6172h7.5508l7.3516 9.2852 11 14.684 10.367 9.0664 7.3477 9.082 7.3516 11 148.38 141.92 52.449 29.633 29-20.734 27.766 18.52-11.566 15.031c30.918 19.535 101.12-25.766 91.383-62.465l-72.883 39.332-30.066-20.832 114.54-101.79c7.3008-42.414 14.633-84.832 21.965-127.25 19.285-1.9336 35.484-8.5 48.586-19.684 55.949 75.602 71.848 166.93 71.73 267.23-0.03125 21.184 16.469 20.602 16.469 20.602l17.449 7.2969 29.484 6.9531 40.383 16.414 23.516 12.484 21.852 4.5664h15.348l16.203-2.8008h29.797l23.336 7.7852 27.848 16.199 17.285 14.465 17.066 27.867 8.6484 33.684 6.9023 60.484 1.9492 36.516 3.668 25.699-1.7344 10.801 3.8984 15.551 8.6328 19.867 11.453 22.25 13.398 28.102 6.6836 25.898-1.9336 25.934-10.367 16.398-7.7812 3.9023-6.6836 5.8164-15.566 20.965-2.8008-20.098-11.668-28.734-17.066-17.469-16.418-20.113-3.6836-22.234-1.9336-23.117-7.7812-20.301-16.398-23.965-10.602-4.7695-3.8828-19.215-1.9336-25.934-0.86719-33.465 5.8164-40.402-1.0664-25.051-8.6484-27-12.535-16.199-17.266-9.5-17.602-4.4648 9.6172 13.531 11.016 29.168 3.4531 18.148v20.285l-1.7344 39.75-3.4688 41.914-5.5977 40.387v20.082l1.9492 9.082 8.8516 14.668 7.5469 9.082 10.816 7.1328 20.086 9.2852 18.398 9.5156 3.4023 4.0859 2.1641 17.281 12.75 47.301 14.699 40.816 14.469 30.484 11.434 17.266 11.664 12.965 6.2695 7.7695 1.5156 9.2812-2.168 9.5156 2.168 6.0508 4.75 2.3828 4.9648-2.1641 3.4531-2.6016 11.016-2.8164 8.8672 1.7344 9.5 9.082 4.75 11.434 4.9648 14.051 3.6836 19.883v11.215l-4.5508 5.418-44.699-22.266-6.2656-8.418-5.1836-9.5-6.918-6.9141-8.6328-4.1172-9.9336-2.8008-7.5664-4.5352-6.2656-7.5664-6.0508-16.199-15.117-35.418-18.148-36.949-15.332-28.281-10.586-16.852-9.9336-12.316-17.051-15.551-15.133-12.098-16.633-16.203-30.883-27.414-25.699-16.633-18.301-14.77-2.668 10.668v23.102l3.0352 23.113 9.7148 23.117 2.7031 17.27 0.76562 7.1328-1.9688 5.6328-18.133 15.965-58.98 49.902-22.668 25.266-14.902 18.148-8.6328 14.902-4.9805 8-7.1367 4.082-6.6797 0.88281-1.7188 2.5859 3.4492 6.0664 5.4023 6.6836 1.9336 9.0664-1.0664 7.7812-1.0859 6.6836-21.816 15.117-15.984 8-12.316 3.25-8.2148-2.168v-4.75l8-35.199 3.0156-5.8477 6.4844-12.953 4.5312-9.2812 0.21875-9.7344 1.9492-14.465 7.1328-9.9336 14.25-11.883 17.934-15.984 10.566-9.7188 19.25-21.598 22.016-27 10.785-15.5 3.4648-19.719-4.75-13.598-12.531-29.602-9.5-23.102-6.6836-18.363-8-35.652-1.1992-38.984-36.383 16.32-40.184 14.465-50.969 14.465-62.215 18.367-30.668 5.4023-31.25 0.11719-4.3828 47.184-3.668 33.699-1.0977 47.098 3.9805 21.066 7.9023 13.703 12.098 10.363 20.52 15.551 21.398 14.035 9.918 9.7344 2.3828 7.332 1.7344 9.2812 6.25 4.9844 6.9141-0.64844 4.5508-4.3359 9.7188-4.5156 6.9141 1.2852 11.434 8.2148 13.836 15.551 10.148 23.102 1.7148 8.8633-3.6484 3.4688-10.816 0.19922-24.617-3.0156-14.684-4.9688-12.965-5.832-14.035-8.8516-8.6484-6.2656-11.883-11.668-12.086-13.816-23.117-23.766-30.031-25.281-14.684-12.734-6.4688-9.5156-4.0977-13.367-1.9688-22.699c-2.8984-14.836-1.9805-11.285-3.8828-23.102l-1.2812-10.582-4.3359-28.102-4.75-32.602-5.6328-42.164-15.531-5.8008-0.65234 6.918-9.2812 6.4805-36.285 29.168c-15.383 8.1992-29.73 15.949-44.5 25.051l-14.699 11.449-19.434 15.332-11.434 10.586-9.7344 14.266-8.8633 18.133-10.566 26.785-4.5508 25.066v18.348l-2.5859 17.27-3.0312 12.531-4.75 4.332-3.2344 7.1367 1.9492 9.7148 4.5352 3.2344 3.668 15.781 0.64844 10.367-7.9844 12.301-16.215 13.184-16.617 7.9844-13.602 3.4492-6.7148-2.3672 1.082-9.7188 6.4688-33.48 5.4141-7.9844 4.3164-11.035 0.65234-11.215-0.21875-15.984 2.6016-17.715 14.031-41.699 5.6172-22.25 10.367-46.434 3.2344-16.203 6.7148-9.2812 9.5-9.5156 6.0352-5.8164 7.8828-9.8867 40.066-60.965 12.969-18.367 3.4492-6.2656v-4.3008l-32.816-58.766-1.9531-12.535 1.9531-20.082 5.3984-23.766v-16.402l-20.102-52.699zm-69.898 9.7031c23.418 0.53516 44.383-6.5977 64.402-13.98-25.77 19.898-30.25 20.082-64.402 13.98z"
              fill-rule="evenodd"
            />
          </g></svg
      ></span>`
    }
    const icon = ACTIVITY_ICONS[field]
    if (!icon) return nothing
    return html`<span class="material-icons" title=${label}>${icon}</span>`
  }

  private renderDogIcon(status: string): unknown {
    if (!status) return nothing
    const s = status.toLowerCase()
    if (s === 'no' || s === 'n') {
      return html`<span class="icon-badge" title="No dogs allowed"
        ><span class="material-icons" style="color:var(--cairn-text-secondary-color,#64748b)"
          >pets</span
        ><span class="material-icons badge" style="color:#dc2626">block</span></span
      >`
    }
    if (s.includes('leash')) {
      return html`<span class="icon-badge" title="Dogs allowed on leash"
        ><span class="material-icons" style="color:var(--cairn-text-secondary-color,#64748b)"
          >pets</span
        ><span class="material-icons badge" style="color:#d97706">warning</span></span
      >`
    }
    return html`<span class="icon-badge" title="Dogs allowed"
      ><span class="material-icons" style="color:var(--cairn-text-secondary-color,#64748b)"
        >pets</span
      ><span class="material-icons badge" style="color:#16a34a">check_circle</span></span
    >`
  }

  private renderCotrexSection(): unknown {
    const ACTIVITY_FIELDS = [
      'hiking',
      'bike',
      'horse',
      'motorcycle',
      'atv',
      'ohv_gt_50',
      'snowmobile',
      'ski',
      'snowshoe',
    ]

    const seen = new Set<string>()
    interface CotrexItem {
      title: string
      subtitle: string
      elevRange: string
      activities: string[]
      dogStatus: string
    }
    const items: CotrexItem[] = []

    for (const f of this.cotrexFeatures) {
      const p = f.properties
      const rawName = ((p.name as string | undefined) ?? '').trim()
      const name = (rawName || (p.manager as string | undefined)) ?? 'Unnamed Trail'
      const key = `${p.feature_id ?? ''}:${name}:${p.trail_num ?? ''}`
      if (seen.has(key)) continue
      seen.add(key)

      const trailNum = p.trail_num ? String(p.trail_num).trim() : ''
      const title = trailNum ? `#${trailNum} · ${name}` : name

      const subtitleParts: string[] = []
      if (p.manager) subtitleParts.push(p.manager as string)
      if (p.surface) {
        const s = String(p.surface)
        subtitleParts.push(s.charAt(0).toUpperCase() + s.slice(1))
      }
      const subtitle = subtitleParts.join(' · ')

      const activities: string[] = []
      for (const field of ACTIVITY_FIELDS) {
        const v = String(p[field] ?? '').toLowerCase()
        if (v && v !== 'no' && v !== 'n') activities.push(field)
      }

      const dogStatus = (p.dogs as string | undefined) ?? ''
      let elevRange = ''
      if (p.min_elevat && p.max_elevat) {
        const minElev = convertElevation(Number(p.min_elevat), this.units)
        const maxElev = convertElevation(Number(p.max_elevat), this.units)
        elevRange = `${minElev} – ${maxElev}`
      }

      items.push({ title, subtitle, elevRange, activities, dogStatus })
    }

    if (items.length === 0) return nothing

    return html`${items.map(
      item => html`
        <div class="compact-item">
          <span class="material-icons" style="color:#0891b2;font-size:18px">hiking</span>
          <div class="compact-body">
            <div class="compact-title-row">
              <span class="compact-title">${item.title}</span>
              ${item.elevRange
                ? html`<span class="compact-right">${item.elevRange}</span>`
                : nothing}
            </div>
            ${item.subtitle ? html`<div class="compact-sub">${item.subtitle}</div>` : nothing}
            ${item.activities.length > 0 || item.dogStatus
              ? html`<div class="compact-sub">
                  <span class="activity-icons"
                    >${item.activities.map(a => this.renderActivityIcon(a))}${this.renderDogIcon(
                      item.dogStatus
                    )}</span
                  >
                </div>`
              : nothing}
          </div>
        </div>
      `
    )}`
  }

  private renderClosureSection(): unknown {
    const seen = new Set<string>()
    interface ClosureItem {
      name: string
      type: string
      wildlifeType: string
      agency: string
      closurePeriod: string
      description: string
      url: string
    }
    const items: ClosureItem[] = []

    for (const f of this.closureFeatures) {
      const p = f.properties
      const name = ((p.name as string | undefined) ?? '').trim()
      if (!name || seen.has(name)) continue
      seen.add(name)
      items.push({
        name,
        type: (p.type as string | undefined) ?? '',
        wildlifeType: (p.wildlife_type as string | undefined) ?? '',
        agency: (p.agency as string | undefined) ?? '',
        closurePeriod: (p.closure_period as string | undefined) ?? '',
        description: (p.description as string | undefined) ?? '',
        url: (p.url as string | undefined) ?? '',
      })
    }

    if (items.length === 0) return nothing

    return html`${items.map(
      item => html`
        <div class="compact-item">
          <span class="material-icons" style="color:#dc2626;font-size:18px">block</span>
          <div class="compact-body">
            <div class="compact-title-row">
              <span class="compact-title">${item.name}</span>
            </div>
            <div class="compact-sub">
              ${item.wildlifeType || item.type}${item.agency ? ` · ${item.agency}` : ''}
            </div>
            ${item.closurePeriod
              ? html`<div class="compact-sub">${item.closurePeriod}</div>`
              : nothing}
            ${item.description
              ? html`<div class="compact-sub" style="white-space:pre-wrap;margin-top:4px">
                  ${item.description}
                </div>`
              : nothing}
            ${item.url
              ? html`<a
                  href="${item.url}"
                  target="_blank"
                  rel="noopener"
                  class="compact-sub"
                  style="display:inline-block;margin-top:2px"
                  >More info</a
                >`
              : nothing}
          </div>
        </div>
      `
    )}`
  }

  private renderFirmsSummary(hotspots: FireFeatureHit[]): unknown {
    if (hotspots.length === 0) return nothing

    const confCounts = new Map<string, number>()
    const sensors = new Set<string>()
    let maxFrp = 0
    for (const h of hotspots) {
      const p = h.properties
      const label = firmsConfidenceLabel((p.confidence as string) ?? '')
      confCounts.set(label, (confCounts.get(label) ?? 0) + 1)
      if (p.sensor) sensors.add(p.sensor as string)
      if (typeof p.frp === 'number' && p.frp > maxFrp) maxFrp = p.frp
    }

    const confParts: string[] = []
    for (const level of ['high', 'nominal', 'low']) {
      const count = confCounts.get(level)
      if (count) confParts.push(`${count} ${level}`)
    }

    return html`
      <div class="compact-item">
        <span class="material-icons" style="color:#fbbf24;font-size:18px">whatshot</span>
        <div class="compact-body">
          <div class="compact-title-row">
            <span class="compact-title"
              >${hotspots.length} FIRMS Detection${hotspots.length !== 1 ? 's' : ''}</span
            >
          </div>
          <div class="compact-sub">${confParts.join(', ')}</div>
          <div class="compact-sub">${[...sensors].join(', ')}</div>
          ${maxFrp > 0 ? html`<div class="compact-sub">Peak FRP: ${maxFrp} MW</div>` : nothing}
        </div>
      </div>
    `
  }

  private renderCellularSection(): unknown {
    const towerPrefix = 'cellular-twr-'
    const effPrefix = 'cellular-eff-'
    interface CellItem {
      carrier: string
      color: string
      techs: string
      subDetails: string[]
    }
    const items: CellItem[] = []
    const seen = new Set<string>()

    for (const f of this.cellularFeatures) {
      const lid = f.layer.id
      const p = f.properties

      if (lid.startsWith(towerPrefix)) {
        const carrier = p.carrier as string
        const coords =
          f.geometry.type === 'Point' ? (f.geometry.coordinates as number[]).join(',') : ''
        const techsRaw =
          (p.technologies as string | undefined) ?? (p.technology as string | undefined) ?? ''
        const key = `tower-${carrier}-${techsRaw}-${coords}`
        if (seen.has(key)) continue
        seen.add(key)
        const techs = techsRaw
        const subDetails: string[] = []
        const rangeM = Number(p.range_m ?? 0)
        if (rangeM > 0) subDetails.push(`Range: ${convertDistance(rangeM, this.units)}`)
        const signal = Number(p.avg_signal ?? 0)
        if (signal > 0) subDetails.push(`Signal: ${signal} dBm`)
        const samples = Number(p.samples ?? 0)
        if (samples > 0) subDetails.push(`${samples} samples`)
        items.push({ carrier, color: CARRIER_COLORS[carrier] ?? '#666', techs, subDetails })
      } else if (lid.startsWith(effPrefix)) {
        const techs = (p.technologies as string | undefined) ?? ''
        const cidMap: Record<string, string> = {
          att: 'att',
          tmobile: 'tmobile',
          verizon: 'verizon',
          uscellular: 'uscellular',
          firstnet: 'firstnet',
        }
        for (const [name, color] of Object.entries(CARRIER_COLORS)) {
          const cid = name.toLowerCase().replace(/[^a-z]/g, '')
          const flagKey = cidMap[cid] ?? cid
          if (!p[flagKey]) continue
          const key = `eff-${name}`
          if (seen.has(key)) continue
          seen.add(key)
          const subDetails: string[] = []
          const tc = Number(p[`tc_${flagKey}`] ?? 0)
          if (tc > 0) subDetails.push(`${tc} tower${tc > 1 ? 's' : ''} in area`)
          const sig = Number(p[`sig_${flagKey}`] ?? 0)
          if (sig > 0) subDetails.push(`Best signal: ${sig} dBm`)
          items.push({ carrier: name, color, techs, subDetails })
        }
      }
    }

    if (items.length === 0) return nothing

    return html`${items.map(
      item => html`
        <div class="compact-item">
          <span class="material-icons" style="color:${item.color};font-size:18px">cell_tower</span>
          <div class="compact-body">
            <div class="compact-title-row">
              <span class="compact-title">${item.carrier}</span>
              ${item.techs ? html`<span class="compact-right">${item.techs}</span>` : nothing}
            </div>
            ${item.subDetails.length > 0
              ? html`<div class="compact-sub">${item.subDetails.join(' · ')}</div>`
              : nothing}
          </div>
        </div>
      `
    )}`
  }

  // ── Sky condition display ──

  private skyIcon(condition: SkyCondition): string {
    const map: Record<SkyCondition, string> = {
      clear: 'clear_day',
      'mostly-clear': 'partly_cloudy_day',
      'partly-cloudy': 'partly_cloudy_day',
      overcast: 'cloud',
      fog: 'foggy',
      drizzle: 'rainy_light',
      rain: 'rainy_heavy',
      'freezing-rain': 'weather_mix',
      snow: 'weather_snowy',
      'snow-showers': 'snowing',
      'rain-showers': 'rainy',
      thunderstorm: 'thunderstorm',
    }
    return map[condition]
  }

  private skyLabel(condition: SkyCondition): string {
    const map: Record<SkyCondition, string> = {
      clear: 'Clear',
      'mostly-clear': 'Mostly Clear',
      'partly-cloudy': 'Partly Cloudy',
      overcast: 'Overcast',
      fog: 'Fog',
      drizzle: 'Drizzle',
      rain: 'Rain',
      'freezing-rain': 'Freezing Rain',
      snow: 'Snow',
      'snow-showers': 'Snow Showers',
      'rain-showers': 'Rain Showers',
      thunderstorm: 'Thunderstorm',
    }
    return map[condition]
  }

  // ── Moon phase ──

  private moonPhaseName(phase: number): string {
    if (phase < 0.03 || phase > 0.97) return 'New Moon'
    if (phase < 0.22) return 'Waxing Crescent'
    if (phase < 0.28) return 'First Quarter'
    if (phase < 0.47) return 'Waxing Gibbous'
    if (phase < 0.53) return 'Full Moon'
    if (phase < 0.72) return 'Waning Gibbous'
    if (phase < 0.78) return 'Last Quarter'
    return 'Waning Crescent'
  }

  // ── Feature measurement ──

  private getFeatureMeasurements(): { distance?: string; area?: string } {
    const result: { distance?: string; area?: string } = {}
    for (const uf of this.userFeatures) {
      const geom = uf.geometry
      if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
        const line =
          geom.type === 'LineString'
            ? lineString(geom.coordinates)
            : multiLineString(geom.coordinates)
        const meters = turfLength(line, { units: 'meters' })
        result.distance = convertDistance(meters, this.units)
      } else if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
        const poly =
          geom.type === 'Polygon' ? polygon(geom.coordinates) : multiPolygon(geom.coordinates)
        const sqMeters = area(poly)
        result.area = convertArea(sqMeters, this.units)
      }
    }
    return result
  }

  // ── Time formatting ──

  private formatTime(date: Date): string {
    if (isNaN(date.getTime())) return '—'
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  }

  // ── Render ──

  override render(): unknown {
    return html`
      <div class="popup-header">
        <div class="tab-bar">
          ${this.renderTabButton('details', 'info')}
          ${this.renderTabButton('measure', 'straighten')}
          ${this.renderTabButton('weather', 'cloud')} ${this.renderTabButton('astro', 'dark_mode')}
          ${this.renderTabButton('hazards', 'warning')}
        </div>
        <button class="close-button" @click=${this.handleClose} title="Close">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="tab-content">
        ${this.activeTab === 'details' ? this.renderDetailsTab() : nothing}
        ${this.activeTab === 'measure' ? this.renderMeasureTab() : nothing}
        ${this.activeTab === 'weather' ? this.renderWeatherTab() : nothing}
        ${this.activeTab === 'astro' ? this.renderAstroTab() : nothing}
        ${this.activeTab === 'hazards' ? this.renderHazardsTab() : nothing}
      </div>
      ${this.expandedPhotoUrl || this.galleryPhotos.length > 0
        ? html`<div
            class="photo-lightbox"
            @click=${() => {
              this.expandedPhotoUrl = null
              this.galleryPhotos = []
            }}
          >
            ${this.galleryPhotos.length > 0
              ? html`
                  <img
                    src="${this.galleryPhotos[this.galleryIndex]}"
                    alt="Photo"
                    class="photo-lightbox-img"
                    @click=${(e: Event) => e.stopPropagation()}
                  />
                  ${this.galleryPhotos.length > 1
                    ? html`
                        <button
                          class="gallery-nav gallery-prev"
                          @click=${(e: Event) => {
                            e.stopPropagation()
                            this.galleryIndex =
                              (this.galleryIndex - 1 + this.galleryPhotos.length) %
                              this.galleryPhotos.length
                          }}
                        >
                          <span class="material-icons">chevron_left</span>
                        </button>
                        <button
                          class="gallery-nav gallery-next"
                          @click=${(e: Event) => {
                            e.stopPropagation()
                            this.galleryIndex = (this.galleryIndex + 1) % this.galleryPhotos.length
                          }}
                        >
                          <span class="material-icons">chevron_right</span>
                        </button>
                        <div class="gallery-counter">
                          ${this.galleryIndex + 1} / ${this.galleryPhotos.length}
                        </div>
                      `
                    : nothing}
                `
              : html`
                  <img
                    src="${this.expandedPhotoUrl}"
                    alt="Wildlife observation"
                    class="photo-lightbox-img"
                  />
                `}
          </div>`
        : nothing}
    `
  }

  /** Draw tools + measure verbs — the merged "act here" surface. */
  /**
   * Features section — the user features at this spot, with the draw toolbar as
   * its "add a feature here" control. Lives near the top of the details tab so
   * it's reachable in the mobile drawer's peek. When nothing was clicked, the
   * list is empty and the toolbar reads as "draw here".
   */
  private renderFeaturesSection(): unknown {
    if (this.userFeatures.length === 0) return nothing
    return html`
      <div class="section-label">Features</div>
      ${this.userFeatures.map(uf => this.renderUserFeature(uf))}
    `
  }

  private renderDrawTools(): unknown {
    return html`
      <div class="action-draw-tools">
        ${DRAW_TOOLS.map(
          t => html`
            <button
              class="action-tool"
              title=${t.title ?? `Draw ${t.label.toLowerCase()}`}
              aria-label=${t.title ?? `Draw ${t.label.toLowerCase()}`}
              @click=${() => this.pickDrawTool(t.tool)}
            >
              <span class="material-icons">${t.icon}</span>
            </button>
          `
        )}
      </div>
    `
  }

  private pickDrawTool(tool: string): void {
    this.dispatchEvent(
      new CustomEvent('menu-draw-tool', { detail: { tool }, bubbles: true, composed: true })
    )
  }

  private renderTabButton(tab: TabId, icon: string): unknown {
    return html`
      <button
        class="tab-button ${this.activeTab === tab ? 'active' : ''}"
        @click=${() => this.setTab(tab)}
        title=${tab.charAt(0).toUpperCase() + tab.slice(1)}
      >
        <span class="material-icons">${icon}</span>
      </button>
    `
  }

  // ── Details tab ──

  private renderDetailsTab(): unknown {
    const landInfo = this.parseLandFeatures()
    const geo = this.geocodeResult
    const hasAddress = geo?.street
    const s = this.settings
    const coords = formatPosition(
      { lat: this.lat, lng: this.lng },
      s.coordinateFormat as CoordinateFormat,
      s.coordinatePrecision as CoordinatePrecision
    )
    // Only the US calls its admin_level-6 region "County"; elsewhere the county
    // field already holds the full local name.
    const countyLine =
      geo?.county && geo.countrycode === 'US' ? `${geo.county} County` : geo?.county

    return html`
      <div class="details-header">
        <div class="details-location">
          ${this.geocodeLoading
            ? html`<div class="loading">
                <div class="spinner"></div>
                Looking up location...
              </div>`
            : geo
              ? html`
                  <div class="location-name">${geo.displayName}</div>
                  ${hasAddress
                    ? html`<div class="location-address">
                        ${geo.housenumber ? `${geo.housenumber} ` : ''}${geo.street}${geo.postcode
                          ? `, ${geo.postcode}`
                          : ''}
                      </div>`
                    : nothing}
                  ${countyLine ? html`<div class="location-address">${countyLine}</div>` : nothing}
                `
              : html`<div class="location-name">${coords}</div>`}
        </div>
        ${this.renderDrawTools()}
      </div>
      ${this.renderActivityTrackSection()}
      ${landInfo.length > 0
        ? html`
            <div class="section-label">Land Ownership</div>
            ${landInfo.map((f, i) => this.renderLandFeature(f, i))}
          `
        : nothing}
      ${this.renderPlssSection()} ${this.renderEmergencySection()} ${this.renderWildlifeSection()}
      ${this.renderClimbingSection()} ${this.renderFeaturesSection()}
      ${this.backcountryFeatures.length > 0
        ? html`
            <div class="section-label">Backcountry</div>
            ${this.renderBackcountrySection()}
          `
        : nothing}
      ${this.cotrexFeatures.length > 0
        ? html`
            <div class="section-label">COTREX</div>
            ${this.renderCotrexSection()}
          `
        : nothing}
      ${this.closureFeatures.length > 0
        ? html`
            <div class="section-label">Wildlife Closures</div>
            ${this.renderClosureSection()}
          `
        : nothing}
      ${this.cellularFeatures.length > 0
        ? html`
            <div class="section-label">Cellular Coverage</div>
            ${this.renderCellularSection()}
          `
        : nothing}
    `
  }

  private renderEmergencySection(): unknown {
    if (this.emergencyFeatures.length === 0) return nothing

    const allTypes: EmergencyDataType[] = [
      'hospitals',
      'fire-stations',
      'law-enforcement',
      'usfs-offices',
    ]
    const grouped = allTypes
      .map(t => ({
        meta: EMERGENCY_META[t],
        facilities: this.emergencyFeatures.filter(f => f.dataType === t).map(f => f.facility),
      }))
      .filter(g => g.facilities.length > 0)

    if (grouped.length === 0) return nothing

    return html`
      <div class="section-label">Emergency Services</div>
      ${grouped.map(
        g => html` ${g.facilities.map(f => this.renderEmergencyFacility(f, g.meta.color))}`
      )}
    `
  }

  private renderEmergencyFacility(f: EmergencyFeatureHit['facility'], color: string): unknown {
    const addrParts = [f.address, f.city && f.state ? `${f.city}, ${f.state}` : null]
      .filter(Boolean)
      .join(', ')
    const mapsUrl = addrParts
      ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addrParts)}`
      : null

    return html`
      <div class="emergency-facility">
        <div class="emergency-dot" style="background:${color}"></div>
        <div class="emergency-facility-info">
          <div class="emergency-facility-name">${f.name}</div>
          <div class="emergency-facility-meta">
            ${mapsUrl
              ? html`<a href="${mapsUrl}" target="_blank" rel="noopener">${addrParts}</a>`
              : nothing}
            ${f.phone ? html`<a href="tel:${f.phone}">${f.phone}</a>` : nothing}
          </div>
        </div>
      </div>
    `
  }

  private renderWildlifeSection(): unknown {
    const hasInat = this.inatObservations.length > 0
    const hasCritHab = this.criticalHabitatFeatures.length > 0
    const nonHazardAlerts = this.npsAlerts.filter(
      a => a.category !== 'Danger' && a.category !== 'Caution'
    )
    const hasAlerts = nonHazardAlerts.length > 0
    if (!hasInat && !hasCritHab && !hasAlerts) return nothing

    return html`
      <div class="section-label">Wildlife & Habitat</div>
      ${hasInat
        ? this.inatObservations.map(
            obs => html`
              <div class="emergency-facility">
                ${obs.thumbUrl
                  ? html`<img
                      src="${obs.thumbUrl}"
                      alt="${obs.commonName}"
                      style="width:36px;height:36px;border-radius:4px;object-fit:cover;flex-shrink:0;cursor:pointer"
                      @click=${() => {
                        this.expandedPhotoUrl = obs.photoUrl ?? obs.thumbUrl ?? null
                      }}
                    />`
                  : html`<div class="emergency-dot" style="background:#16a34a"></div>`}
                <div class="emergency-facility-info">
                  <div class="emergency-facility-name">${obs.commonName}</div>
                  <div class="emergency-facility-meta">
                    <em>${obs.scientificName}</em>
                    ${obs.observedOn ? html`<span>${obs.observedOn}</span>` : nothing}
                    ${obs.uri
                      ? html`<a href="${obs.uri}" target="_blank" rel="noopener">iNaturalist</a>`
                      : nothing}
                  </div>
                </div>
              </div>
            `
          )
        : nothing}
      ${hasCritHab
        ? this.criticalHabitatFeatures.map(f => {
            const p = f.properties
            const sciname = (p.sciname as string) || ''
            const status = (p.status as string) || ''
            const color = status === 'Endangered' ? '#dc2626' : '#ea580c'
            const photoUrl = this.speciesPhotos.get(sciname)
            return html`
              <div class="emergency-facility">
                ${photoUrl
                  ? html`<img
                      src="${photoUrl}"
                      alt="${(p.comname as string) || sciname}"
                      style="width:36px;height:36px;border-radius:4px;object-fit:cover;flex-shrink:0;cursor:pointer"
                      @click=${() => {
                        this.expandedPhotoUrl = photoUrl.replace('square', 'medium')
                      }}
                    />`
                  : html`<div class="emergency-dot" style="background:${color}"></div>`}
                <div class="emergency-facility-info">
                  <div class="emergency-facility-name">
                    ${(p.comname as string) || sciname || 'Critical Habitat'}
                  </div>
                  <div class="emergency-facility-meta">
                    ${sciname ? html`<em>${sciname}</em>` : nothing}
                    <span
                      style="display:inline-block;padding:1px 4px;border-radius:3px;background:${color};color:#fff;font-size:0.7rem"
                      >${status || 'Listed'}</span
                    >
                  </div>
                </div>
              </div>
            `
          })
        : nothing}
      ${hasAlerts
        ? nonHazardAlerts.map(alert => {
            const color =
              alert.category === 'Danger' || alert.category === 'Park Closure'
                ? '#dc2626'
                : alert.category === 'Caution'
                  ? '#d97706'
                  : '#2563eb'
            return html`
              <div class="emergency-facility">
                <div class="emergency-dot" style="background:${color}"></div>
                <div class="emergency-facility-info">
                  <div class="emergency-facility-name">${alert.title}</div>
                  ${alert.description
                    ? html`<div
                        style="font-size:0.75rem;color:var(--cairn-text-secondary-color,#64748b);margin:2px 0 4px;line-height:1.3"
                      >
                        ${alert.description}
                      </div>`
                    : nothing}
                  <div class="emergency-facility-meta">
                    <span
                      style="display:inline-block;padding:1px 4px;border-radius:3px;background:${color};color:#fff;font-size:0.7rem"
                      >${alert.category}</span
                    >
                    ${alert.parkName ? html`<span>${alert.parkName}</span>` : nothing}
                    ${alert.url
                      ? html`<a href="${alert.url}" target="_blank" rel="noopener">More info</a>`
                      : nothing}
                  </div>
                </div>
              </div>
            `
          })
        : nothing}
    `
  }

  private async loadNearbyCrags(): Promise<void> {
    this.climbingLoading = true
    await openBetaSearchService.ensureLoaded()

    if (this.clickedCrags.length > 0) {
      this.nearbyCrags = this.clickedCrags.map(c => ({
        u: c.uuid,
        n: c.name,
        c: [this.lat, this.lng] as [number, number],
        t: c.totalClimbs,
        p: [],
        d: c.disciplines as { s?: number; r?: number; b?: number; a?: number; t?: number },
      }))
      this.expandedCragUuid = null
    }
    this.climbingLoading = false
  }

  private async loadCragDetail(uuid: string): Promise<void> {
    if (this.cragDetails.has(uuid) || this.cragDetailsLoading.has(uuid)) return
    const loading = new Set(this.cragDetailsLoading)
    loading.add(uuid)
    this.cragDetailsLoading = loading
    try {
      const detail = await openBetaSearchService.getAreaDetail(uuid)
      if (detail) {
        this.cragDetails = new Map(this.cragDetails).set(uuid, detail)
      } else {
        this.cragDetailsFailed = new Set(this.cragDetailsFailed).add(uuid)
      }
    } catch {
      this.cragDetailsFailed = new Set(this.cragDetailsFailed).add(uuid)
    }
    const done = new Set(this.cragDetailsLoading)
    done.delete(uuid)
    this.cragDetailsLoading = done
  }

  private toggleCragExpand(uuid: string): void {
    if (this.expandedCragUuid === uuid && !this.cragDrillCurrent) {
      this.expandedCragUuid = null
      this.cragDrillStack = []
      this.cragDrillCurrent = null
    } else {
      this.expandedCragUuid = uuid
      this.cragDrillStack = []
      this.cragDrillCurrent = null
      void this.loadCragDetail(uuid)
    }
  }

  private drillIntoCragChild(
    parentUuid: string,
    parentName: string,
    childUuid: string,
    childName: string
  ): void {
    this.cragDrillStack = [...this.cragDrillStack, { uuid: parentUuid, name: parentName }]
    this.cragDrillCurrent = { uuid: childUuid, name: childName }
    void this.loadCragDetail(childUuid)
  }

  private drillBackCrag(): void {
    const prev = this.cragDrillStack[this.cragDrillStack.length - 1]
    if (!prev) {
      this.cragDrillCurrent = null
      return
    }
    this.cragDrillStack = this.cragDrillStack.slice(0, -1)
    this.cragDrillCurrent = this.cragDrillStack.length > 0 ? prev : null
  }

  private renderClimbingSection(): unknown {
    if (this.nearbyCrags.length === 0 && !this.climbingLoading) return nothing

    return html`
      <div class="section-label">Climbing</div>
      ${this.climbingLoading && this.nearbyCrags.length === 0
        ? html`<div class="loading" style="padding:4px 0">
            <div class="spinner"></div>
            Loading climbing areas...
          </div>`
        : nothing}
      ${this.nearbyCrags.map(crag => {
        const isExpanded = this.expandedCragUuid === crag.u
        const isLoading = this.cragDetailsLoading.has(crag.u)

        const activeUuid = this.cragDrillCurrent?.uuid ?? crag.u
        const detail = this.cragDetails.get(activeUuid)
        const activeLoading = this.cragDetailsLoading.has(activeUuid)

        const cragMpQuery = encodeURIComponent(crag.n)
        return html`
          <div
            class="compact-item"
            style="cursor:pointer"
            @click=${() => this.toggleCragExpand(crag.u)}
          >
            <span class="material-icons" style="color:#e65100;font-size:18px">terrain</span>
            <div class="compact-body" style="flex:1">
              <div class="compact-title">
                <a
                  href="https://www.mountainproject.com/search?q=${cragMpQuery}"
                  target="_blank"
                  rel="noopener"
                  style="color:inherit;text-decoration:underline"
                  @click=${(e: Event) => e.stopPropagation()}
                  >${crag.n}<span style="font-size:0.6em;vertical-align:super;margin-left:1px"
                    >↗</span
                  ></a
                >
              </div>
              <div class="compact-sub">
                ${crag.t} climb${crag.t !== 1 ? 's' : ''}${this.formatDisciplines(crag)}
              </div>
            </div>
            <span
              class="material-icons"
              style="font-size:16px;color:var(--cairn-text-secondary-color,#64748b);transition:transform 0.2s;${isExpanded
                ? 'transform:rotate(90deg)'
                : ''}"
              >chevron_right</span
            >
          </div>
          ${isExpanded
            ? html`
                <div class="crag-detail">
                  ${this.cragDrillCurrent
                    ? html`
                        <div
                          style="display:flex;align-items:center;gap:4px;padding:4px 8px;cursor:pointer;font-size:0.8rem;color:var(--cairn-primary-color,#1976d2)"
                          @click=${(e: Event) => {
                            e.stopPropagation()
                            this.drillBackCrag()
                          }}
                        >
                          <span class="material-icons" style="font-size:14px">arrow_back</span>
                          Back
                        </div>
                        <div style="padding:0 8px 4px;font-weight:600;font-size:0.85rem">
                          ${this.cragDrillCurrent.name}
                        </div>
                      `
                    : nothing}
                  ${activeLoading || isLoading
                    ? html`<div class="loading" style="padding:4px 8px">
                        <div class="spinner"></div>
                      </div>`
                    : detail
                      ? this.renderCragDetail(detail, crag.u)
                      : html`<div
                          style="padding:4px 8px;color:var(--cairn-text-secondary-color,#64748b);font-size:0.8rem"
                        >
                          Failed to load details
                        </div>`}
                </div>
              `
            : nothing}
        `
      })}
    `
  }

  private formatDisciplines(crag: {
    d?: { s?: number; r?: number; b?: number; a?: number; t?: number }
  }): string {
    const d = crag.d
    if (!d) return ''
    const parts: string[] = []
    if (d.s) parts.push(`${d.s} sport`)
    if (d.r) parts.push(`${d.r} trad`)
    if (d.b) parts.push(`${d.b} boulder`)
    if (d.a) parts.push(`${d.a} alpine`)
    if (d.t) parts.push(`${d.t} TR`)
    return parts.length > 0 ? ` · ${parts.join(', ')}` : ''
  }

  private matchesClimbFilter(climb: OpenBetaClimb): boolean {
    const f = this.climbFilter
    if (!f) return true
    if (f.typeMask !== 0) {
      let climbBits = 0
      for (const ct of CLIMB_TYPES) {
        if (climb.type[ct.key]) climbBits |= ct.bit
      }
      if ((climbBits & f.typeMask) === 0) return false
    }
    const grade = parseGradeNumeric(
      climb.grades.yds ?? climb.grades.vscale ?? climb.grades.french ?? ''
    )
    if (grade >= 0) {
      if (grade >= V_OFFSET) {
        const vIdx = grade - V_OFFSET
        if (vIdx < f.vMin || vIdx > f.vMax) return false
      } else {
        if (grade < f.ydsMin || grade > f.ydsMax) return false
      }
    }
    return true
  }

  private renderCragDetail(detail: OpenBetaAreaDetail, rootUuid: string): unknown {
    const children = detail.children ?? []
    const allClimbs = detail.climbs ?? []
    const climbs = this.climbFilter ? allClimbs.filter(c => this.matchesClimbFilter(c)) : allClimbs
    const media = detail.media ?? []
    const hasChildren = children.length > 0
    const hasClimbs = climbs.length > 0
    return html`
      ${media.length > 0
        ? html`
            <div class="crag-photos">
              ${media.slice(0, 4).map(
                (m, i) => html`
                  <img
                    src="${m.mediaUrl}"
                    alt="${detail.areaName}"
                    class="crag-thumb"
                    @click=${(e: Event) => {
                      e.stopPropagation()
                      this.galleryPhotos = media.map(x => x.mediaUrl)
                      this.galleryIndex = i
                    }}
                  />
                `
              )}
            </div>
          `
        : nothing}
      ${hasChildren
        ? html`
            <div class="climb-list-label">
              ${children.length} sub-area${children.length !== 1 ? 's' : ''}
            </div>
            ${children.slice(0, 15).map(
              c => html`
                <div
                  class="climb-row"
                  style="cursor:pointer"
                  @click=${(e: Event) => {
                    e.stopPropagation()
                    this.drillIntoCragChild(
                      this.cragDrillCurrent?.uuid ?? rootUuid,
                      this.cragDrillCurrent?.name ?? detail.areaName,
                      c.uuid,
                      c.areaName
                    )
                  }}
                >
                  <span class="material-icons" style="color:#e65100;font-size:14px">folder</span>
                  <span class="climb-name">${c.areaName}</span>
                  <span class="climb-grade"
                    >${c.totalClimbs} climb${c.totalClimbs !== 1 ? 's' : ''}</span
                  >
                  <span
                    class="material-icons"
                    style="font-size:14px;color:var(--cairn-text-secondary-color,#64748b)"
                    >chevron_right</span
                  >
                </div>
              `
            )}
            ${children.length > 15
              ? html`<div class="climb-more">+ ${children.length - 15} more</div>`
              : nothing}
          `
        : nothing}
      ${hasClimbs
        ? html`
            <div class="climb-list-label">
              ${climbs.length} route${climbs.length !== 1 ? 's' : ''}
            </div>
            ${climbs.slice(0, 20).map(climb => {
              const grade =
                climb.grades.yds ??
                climb.grades.vscale ??
                climb.grades.french ??
                climb.grades.uiaa ??
                climb.grades.font ??
                ''
              const types: string[] = []
              if (climb.type.sport) types.push('Sport')
              if (climb.type.trad) types.push('Trad')
              if (climb.type.bouldering) types.push('Boulder')
              if (climb.type.alpine) types.push('Alpine')
              if (climb.type.ice) types.push('Ice')
              if (climb.type.mixed) types.push('Mixed')
              if (climb.type.aid) types.push('Aid')
              if (climb.type.tr) types.push('TR')
              const climbMpQuery = encodeURIComponent(`${climb.name} ${detail.areaName}`)
              return html`
                <div class="climb-row">
                  <span class="climb-name"
                    ><a
                      href="https://www.mountainproject.com/search?q=${climbMpQuery}"
                      target="_blank"
                      rel="noopener"
                      style="color:inherit;text-decoration:underline"
                      @click=${(e: Event) => e.stopPropagation()}
                      >${climb.name}</a
                    ></span
                  >
                  ${grade ? html`<span class="climb-grade">${grade}</span>` : nothing}
                  ${types.length > 0
                    ? html`<span class="climb-type">${types.join('/')}</span>`
                    : nothing}
                  ${climb.length > 0
                    ? html`<span class="climb-length">${climb.length}ft</span>`
                    : nothing}
                </div>
              `
            })}
            ${climbs.length > 20
              ? html`<div class="climb-more">+ ${climbs.length - 20} more routes</div>`
              : nothing}
          `
        : nothing}
    `
  }

  private renderActivityTrackSection(): unknown {
    const info = this.activityTrackInfo
    if (!info) return nothing

    const formatDuration = (s: number): string => {
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = Math.floor(s % 60)
      if (h > 0) return `${h}h ${m}m`
      if (m > 0) return `${m}m ${sec}s`
      return `${sec}s`
    }

    return html`
      <div class="section-label">Activity Track</div>
      <div class="detail-row">
        <span>Distance</span>
        <span>${convertDistance(info.distanceM, this.units)}</span>
      </div>
      ${info.elevationM !== null
        ? html`<div class="detail-row">
            <span>Elevation</span>
            <span>${convertElevation(info.elevationM, this.units)}</span>
          </div>`
        : nothing}
      <div class="detail-row">
        <span>Elevation Gain</span>
        <span>${convertElevation(info.elevationGainM, this.units)}</span>
      </div>
      <div class="detail-row">
        <span>Elevation Loss</span>
        <span>${convertElevation(info.elevationLossM, this.units)}</span>
      </div>
      <div class="detail-row">
        <span>Elapsed Time</span>
        <span>${formatDuration(info.elapsedS)}</span>
      </div>
      ${info.rest
        ? html`
            <div class="detail-row">
              <span>Rest Stop</span>
              <span>${formatDuration(info.rest.duration)}</span>
            </div>
          `
        : nothing}
    `
  }

  private renderUserFeature(uf: UserFeatureInfo): unknown {
    const geom = uf.geometry
    let measurement = ''
    if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
      const line =
        geom.type === 'LineString'
          ? lineString(geom.coordinates)
          : multiLineString(geom.coordinates)
      const meters = turfLength(line, { units: 'meters' })
      measurement = convertDistance(meters, this.units)
    } else if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
      const poly =
        geom.type === 'Polygon' ? polygon(geom.coordinates) : multiPolygon(geom.coordinates)
      const sqMeters = area(poly)
      measurement = convertArea(sqMeters, this.units)
    } else if (geom.type === 'Point' && uf.elevation !== undefined) {
      measurement = convertElevation(uf.elevation, this.units)
    }

    const assoc = uf.association
    return html`
      <div class="user-feature">
        <div class="user-feature-header">
          <div class="user-feature-main">
            <span class="material-icons user-feature-icon">${userFeatureIcon(uf.type)}</span>
            <div class="user-feature-text">
              <span class="user-feature-name">${uf.name}</span>
              ${measurement || assoc
                ? html`<div class="user-feature-subtitle">
                    ${measurement
                      ? html`<span class="user-feature-measurement">${measurement}</span>`
                      : nothing}
                    ${measurement && assoc
                      ? html`<span class="user-feature-sep">·</span>`
                      : nothing}
                    ${assoc
                      ? html`<button
                          class="user-feature-link"
                          title=${assoc.name}
                          @click=${() => this.handleOpenEntity(assoc.kind, assoc.id)}
                        >
                          ${assoc.kind === 'activity' ? 'Activity' : 'Plan'}
                        </button>`
                      : nothing}
                  </div>`
                : nothing}
            </div>
          </div>
          <div class="user-feature-actions">
            <button
              class="user-feature-edit"
              @click=${() => this.handleViewFeature(uf.id)}
              title="View measurements"
              aria-label="View measurements for this feature"
            >
              <span class="material-icons">visibility</span>
            </button>
            <button
              class="user-feature-edit"
              @click=${() => this.handleEditFeature(uf.id)}
              title="Edit this feature"
              aria-label="Edit this feature"
            >
              <span class="material-icons">edit</span>
            </button>
          </div>
        </div>
      </div>
    `
  }

  private handleOpenEntity(kind: 'activity' | 'plan', id: string): void {
    this.dispatchEvent(
      new CustomEvent('open-entity', { bubbles: true, composed: true, detail: { kind, id } })
    )
    this.handleClose()
  }

  private renderLandFeature(f: LandFeatureInfo, index: number): unknown {
    if (f.layerType === 'padus') {
      const desEntry = f.desCode ? DESIGNATION_TYPES[f.desCode] : undefined
      const accessEntry = f.accessCode ? ACCESS_LEVELS[f.accessCode] : undefined
      const gapEntry = f.gapCode ? GAP_STATUS[f.gapCode] : undefined
      return html`
        <div class="land-feature">
          <div class="land-feature-name">${f.name ?? 'Unknown Unit'}</div>
          ${desEntry
            ? html`<div class="land-feature-detail">
                ${this.renderGlossaryTerm(`des-${index}`, desEntry)}
              </div>`
            : f.desCode
              ? html`<div class="land-feature-detail">${f.desCode}</div>`
              : nothing}
          ${f.manager
            ? html`<div class="land-feature-detail">Manager: ${f.manager}</div>`
            : nothing}
          ${accessEntry
            ? html`<div class="land-feature-detail">
                ${this.renderGlossaryTerm(`access-${index}`, accessEntry)}
              </div>`
            : nothing}
          ${gapEntry
            ? html`<div class="land-feature-detail">
                ${this.renderGlossaryTerm(`gap-${index}`, gapEntry)}
              </div>`
            : nothing}
          ${f.area ? html`<div class="land-feature-detail">Area: ${f.area}</div>` : nothing}
        </div>
      `
    }

    const agencyEntry = f.agency ? BLM_AGENCIES[f.agency] : undefined
    return html`
      <div class="land-feature">
        <div class="land-feature-name">
          ${agencyEntry
            ? this.renderGlossaryTerm(`agency-${index}`, agencyEntry)
            : (f.agency ?? 'Unknown Agency')}
        </div>
        ${f.unit ? html`<div class="land-feature-detail">${f.unit}</div>` : nothing}
        ${f.stateCode
          ? html`<div class="land-feature-detail">State: ${f.stateCode}</div>`
          : nothing}
        ${f.area ? html`<div class="land-feature-detail">Area: ${f.area}</div>` : nothing}
      </div>
    `
  }

  /** Public Land Survey System — township + section legal description. */
  private renderPlssSection(): unknown {
    const plss = this.plss
    if (!plss || (!plss.township && !plss.section)) return nothing
    return html`
      <div class="section-label">Survey (PLSS)</div>
      ${plss.township
        ? html`<div class="detail-row">
            <span class="detail-label">Township</span>
            <span class="detail-value">${plss.township}</span>
          </div>`
        : nothing}
      ${plss.section
        ? html`<div class="detail-row">
            <span class="detail-label">Section</span>
            <span class="detail-value">${plss.section}</span>
          </div>`
        : nothing}
    `
  }

  // ── Measurement tab ──

  private renderMeasureTab(): unknown {
    const s = this.settings
    const pos: Position = { lat: this.lat, lng: this.lng }
    const coords = formatPosition(
      pos,
      s.coordinateFormat as CoordinateFormat,
      s.coordinatePrecision as CoordinatePrecision
    )

    const elevText =
      this.elevation !== null
        ? convertElevation(this.elevation, this.units)
        : this.elevationLoading
          ? 'Loading...'
          : 'N/A'

    const measurements = this.getFeatureMeasurements()

    let declinationText = ''
    try {
      const model = geomagnetism.model()
      const info = model.point([this.lat, this.lng])
      const dec = info.decl
      const dir = dec >= 0 ? 'E' : 'W'
      declinationText = `${Math.abs(dec).toFixed(1)}° ${dir}`
    } catch {
      declinationText = 'N/A'
    }

    return html`
      <div class="section-label">Location</div>
      <div class="detail-row">
        <span class="detail-label">Coordinates</span>
        <span class="detail-value">${coords}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Elevation</span>
        <span class="detail-value">${elevText}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label">Mag. Declination</span>
        <span class="detail-value">${declinationText}</span>
      </div>
      ${measurements.distance
        ? html`
            <div class="section-label">Feature</div>
            <div class="detail-row">
              <span class="detail-label">Distance</span>
              <span class="detail-value">${measurements.distance}</span>
            </div>
          `
        : nothing}
      ${measurements.area
        ? html`
            <div class="section-label">Feature</div>
            <div class="detail-row">
              <span class="detail-label">Area</span>
              <span class="detail-value">${measurements.area}</span>
            </div>
          `
        : nothing}
    `
  }

  // ── Weather tab ──

  private renderWeatherTab(): unknown {
    if (this.weatherLoading) {
      return html`<div class="loading">
        <div class="spinner"></div>
        Loading weather...
      </div>`
    }

    const c = this.weather
    const snowInfo = this.getSnowInfo()
    return html`
      ${c
        ? html`
            <div class="weather-main">
              <span class="material-icons weather-icon">${this.skyIcon(c.skyCondition)}</span>
              <div>
                <div class="weather-temp">${this.tempVal(c.temperature)}${this.tempUnit}</div>
                <div class="weather-feels">
                  Feels like ${this.tempVal(c.apparentTemperature)}${this.tempUnit}
                </div>
              </div>
            </div>
            <div class="weather-condition">${this.skyLabel(c.skyCondition)}</div>
            <div class="weather-details">
              <span class="weather-detail">
                <span class="material-icons">air</span>
                ${this.windVal(c.windSpeed)} ${this.windUnit}
                ${c.windGusts > c.windSpeed + 5
                  ? html`, gusts ${this.windVal(c.windGusts)}`
                  : nothing}
              </span>
              <span class="weather-detail">
                <span class="material-icons">water_drop</span>
                ${c.humidity}%
              </span>
              <span class="weather-detail">
                <span class="material-icons">cloud</span>
                ${c.cloudCover}%
              </span>
            </div>
            ${this.renderPopupForecastBlocks()}
          `
        : html`<div class="no-data">Weather data unavailable</div>`}
      ${snowInfo ? this.renderSnowSection(snowInfo) : nothing}
    `
  }

  private static lightningRiskColor(risk: LightningRisk): string {
    switch (risk) {
      case 'high':
        return '#ef4444'
      case 'moderate':
        return '#eab308'
      case 'low':
        return '#9ca3af'
    }
  }

  private renderPopupForecastBlocks(): unknown {
    if (!this.weatherData?.hourly.length) return nothing

    const now = Date.now()
    const in24h = now + 24 * 60 * 60 * 1000
    const upcoming = this.weatherData.hourly.filter(h => {
      const t = new Date(h.time).getTime()
      return t >= now && t <= in24h
    })
    if (upcoming.length === 0) return nothing

    // Aggregate into 3-hour blocks
    const blocks: Array<{
      label: string
      temp: number
      wind: number
      gusts: number
      windDir: number
      precip: number
      snow: number
      sky: SkyCondition
      cape: number
    }> = []

    for (let i = 0; i < upcoming.length; i += 3) {
      const chunk = upcoming.slice(i, i + 3)
      const midIdx = Math.min(1, chunk.length - 1)
      const mid = chunk[midIdx]
      blocks.push({
        label: new Date(mid.time).toLocaleTimeString('en-US', { hour: 'numeric' }),
        temp: mid.temperature,
        wind: mid.windSpeed,
        gusts: mid.windGusts,
        windDir: mid.windDirection,
        precip: chunk.reduce((s, h) => s + h.precipAmount, 0),
        snow: chunk.reduce((s, h) => s + h.snowfall, 0),
        sky: mid.skyCondition,
        cape: Math.max(...chunk.map(h => h.cape)),
      })
    }

    return html`
      <div class="popup-forecast-label">Next 24 hours</div>
      <div class="popup-forecast-blocks">
        ${blocks.map(b => {
          const risk = capeToLightningRisk(b.cape)
          return html`
            <div class="popup-forecast-block">
              <span class="popup-block-time">${b.label}</span>
              <span class="material-icons popup-block-icon">${this.skyIcon(b.sky)}</span>
              <span class="popup-block-temp">${this.tempVal(b.temp)}°</span>
              <div class="popup-block-wind">
                <span class="material-icons">air</span>
                ${this.windVal(b.wind)}${b.gusts > b.wind + 5
                  ? html`-${this.windVal(b.gusts)}`
                  : nothing}
              </div>
              ${risk !== 'low'
                ? html`<span
                    class="material-icons popup-block-lightning"
                    style="color: ${MapContextPopup.lightningRiskColor(risk)}"
                    >electric_bolt</span
                  >`
                : nothing}
            </div>
          `
        })}
      </div>
    `
  }

  // ── Snow info (SNODAS depth + VIIRS NDSI coverage + Open-Meteo forecast) ──

  private static readonly NDSI_WMS_BASE =
    'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi'
  private static readonly NDSI_LAYERS = [
    'VIIRS_SNPP_NDSI_Snow_Cover',
    'VIIRS_NOAA20_NDSI_Snow_Cover',
  ] as const

  private static readonly NDSI_COLORMAP: Map<string, number> = (() => {
    const m = new Map<string, number>()
    const bands: [number, number, number][] = [
      [240, 240, 0],
      [240, 210, 20],
      [240, 180, 40],
      [240, 150, 60],
    ]
    for (const [r, g, startNdsi] of bands) {
      for (let i = 0; i < 20; i++) {
        m.set(`${r},${g},${128 + i}`, startNdsi + i + 1)
      }
    }
    for (let i = 81; i <= 89; i++) {
      m.set(`240,${120 + i - 81},128`, i)
    }
    for (let i = 90; i <= 99; i++) {
      const off = i - 90
      const g = 128 + Math.ceil(off / 2)
      const b = 128 + Math.ceil((off + 1) / 2)
      m.set(`240,${g},${b}`, i)
    }
    m.set('255,0,0', 100)
    return m
  })()

  private static ndsiFromPixel(r: number, g: number, b: number, a: number): number | null {
    if (a === 0) return null
    return MapContextPopup.NDSI_COLORMAP.get(`${r},${g},${b}`) ?? null
  }

  private static readonly NDSI_SAMPLE_SIZE = 3

  private async queryNdsiAtPoint(lat: number, lng: number): Promise<number | null> {
    const date = timelineService.getCurrentDateISO()
    const mx = (lng * 20037508.34) / 180
    const my = (Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) * 20037508.34) / Math.PI
    const delta = 1000
    const bbox = `${mx - delta},${my - delta},${mx + delta},${my + delta}`
    const sz = MapContextPopup.NDSI_SAMPLE_SIZE

    const results = await Promise.all(
      MapContextPopup.NDSI_LAYERS.map(async layer => {
        try {
          const url =
            `${MapContextPopup.NDSI_WMS_BASE}?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap` +
            `&LAYERS=${layer}&SRS=EPSG:3857&BBOX=${bbox}` +
            `&WIDTH=${sz}&HEIGHT=${sz}&FORMAT=image/png&TRANSPARENT=true&TIME=${date}`
          const resp = await fetch(url, { signal: AbortSignal.timeout(5000) })
          if (!resp.ok) return null
          const blob = await resp.blob()
          const bmp = await createImageBitmap(blob)
          const canvas = new OffscreenCanvas(sz, sz)
          const ctx = canvas.getContext('2d')!
          ctx.drawImage(bmp, 0, 0)
          const data = ctx.getImageData(0, 0, sz, sz).data
          let best: number | null = null
          for (let i = 0; i < data.length; i += 4) {
            const v = MapContextPopup.ndsiFromPixel(data[i], data[i + 1], data[i + 2], data[i + 3])
            if (v !== null && (best === null || v > best)) best = v
          }
          return best
        } catch {
          return null
        }
      })
    )
    const valid = results.filter((v): v is number => v !== null)
    return valid.length > 0 ? Math.max(...valid) : null
  }

  private getSnowInfo(): {
    snodasDepthIn: number | null
    ndsi: number | null
    forecastPrecipProb: number
    forecastSnowNext24: number
  } | null {
    const s = this.snowData
    const snodasDepthIn = s?.snodasDepthMm != null ? s.snodasDepthMm / 25.4 : null
    const ndsi = s?.ndsi ?? null

    let forecastPrecipProb = 0
    let forecastSnowNext24 = 0

    if (this.weatherData) {
      const now = Date.now()
      if (this.weatherData.hourly.length > 0) {
        let closest = this.weatherData.hourly[0]
        let closestDiff = Math.abs(new Date(closest.time).getTime() - now)
        for (const h of this.weatherData.hourly) {
          const diff = Math.abs(new Date(h.time).getTime() - now)
          if (diff < closestDiff) {
            closest = h
            closestDiff = diff
          }
        }
        forecastPrecipProb = closest.precipProbability
      }
      for (const h of this.weatherData.hourly) {
        const t = new Date(h.time).getTime()
        if (t >= now && t <= now + 24 * 60 * 60 * 1000) {
          forecastSnowNext24 += h.snowfall
        }
      }
    }

    const hasAny =
      snodasDepthIn !== null || ndsi !== null || forecastPrecipProb > 0 || forecastSnowNext24 > 0

    if (!hasAny) return null

    return { snodasDepthIn, ndsi, forecastPrecipProb, forecastSnowNext24 }
  }

  private formatDepth(inches: number): string {
    if (this.units === 'metric') {
      const cm = inches * 2.54
      return cm < 1 ? `${cm.toFixed(1)} cm` : `${Math.round(cm)} cm`
    }
    return inches < 1 ? `${inches.toFixed(1)}"` : `${Math.round(inches)}"`
  }

  private static ndsiLabel(score: number): string {
    if (score >= 80) return 'Full snow cover'
    if (score >= 60) return 'Snow likely'
    if (score >= 40) return 'Snow detected'
    if (score >= 20) return 'Patchy snow'
    return 'Minimal snow'
  }

  private static ndsiDetail(score: number): string {
    const rounded = Math.round(score)
    if (score >= 80)
      return `NDSI ${rounded} — Strong snow signature across the area. Ground is almost certainly snow-covered.`
    if (score >= 60)
      return `NDSI ${rounded} — Moderate-to-strong snow signature. Most of the area is snow-covered, some bare ground possible.`
    if (score >= 40)
      return `NDSI ${rounded} — Snow is present but may be thin, patchy, or mixed with vegetation. Expect variable conditions.`
    if (score >= 20)
      return `NDSI ${rounded} — Weak snow signal. Sparse or very thin coverage — ground may be mostly bare.`
    return `NDSI ${rounded} — Very weak signal. Little to no snow detected by satellite.`
  }

  private renderSnowSection(snow: {
    snodasDepthIn: number | null
    ndsi: number | null
    forecastPrecipProb: number
    forecastSnowNext24: number
  }): unknown {
    return html`
      <div class="weather-snow">
        <div class="weather-snow-header">
          <span class="material-icons" style="font-size:18px">weather_snowy</span>
          Snow
        </div>
        <div class="weather-details">
          ${snow.snodasDepthIn !== null
            ? html`<span class="weather-detail">
                <span class="material-icons">straighten</span>
                <span class="glossary-term" @click=${() => this.toggleTerm('snodas-depth')}
                  >${this.formatDepth(snow.snodasDepthIn)} depth</span
                >
                ${this.expandedTerms.has('snodas-depth')
                  ? html`<div class="glossary-def">
                      SNODAS modeled snow depth — a daily 1km gridded estimate from NOAA that
                      assimilates ground station, satellite, and radar data. Represents average
                      depth across the grid cell, not point measurements.
                    </div>`
                  : nothing}
              </span>`
            : nothing}
          ${snow.ndsi !== null
            ? html`<span class="weather-detail">
                <span class="material-icons">satellite_alt</span>
                <span class="glossary-term" @click=${() => this.toggleTerm('ndsi-cover')}
                  >${MapContextPopup.ndsiLabel(snow.ndsi)}</span
                >
                ${this.expandedTerms.has('ndsi-cover')
                  ? html`<div class="glossary-def">${MapContextPopup.ndsiDetail(snow.ndsi)}</div>`
                  : nothing}
              </span>`
            : nothing}
          ${snow.forecastPrecipProb > 0
            ? html`<span class="weather-detail">
                <span class="material-icons">percent</span>
                ${snow.forecastPrecipProb}% precip chance
              </span>`
            : nothing}
          ${snow.forecastSnowNext24 > 0
            ? html`<span class="weather-detail">
                <span class="material-icons">schedule</span>
                ${this.formatDepth(snow.forecastSnowNext24)} next 24h
              </span>`
            : nothing}
        </div>
      </div>
    `
  }

  // ── Astro tab ──

  private arcPoint(angle: number, cx: number, cy: number, r: number): { x: number; y: number } {
    return { x: cx + r * Math.cos(angle), y: cy - r * Math.sin(angle) }
  }

  private renderArcTick(angle: number, cx: number, cy: number, r: number, half: number): unknown {
    if (isNaN(angle)) return nothing
    const inner = this.arcPoint(angle, cx, cy, r - half - 3)
    const outer = this.arcPoint(angle, cx, cy, r + half + 3)
    return svg`<line
      x1=${inner.x} y1=${inner.y} x2=${outer.x} y2=${outer.y}
      stroke="var(--cairn-text-muted-color, #94a3b8)" stroke-width="1"
    />`
  }

  private selectAstroTerm(key: string): void {
    this.activeAstroKey = this.activeAstroKey === key ? null : key
  }

  private renderArcLabel(
    key: string,
    time: Date,
    angle: number,
    cx: number,
    cy: number,
    labelR: number
  ): unknown {
    if (isNaN(angle)) return nothing
    const pos = this.arcPoint(angle, cx, cy, labelR)
    const aDeg = (angle * 180) / Math.PI
    const rotDeg = aDeg > 90 ? 180 - aDeg : -aDeg
    const isActive = this.activeAstroKey === key
    return svg`<text
      class="arc-label${isActive ? ' active' : ''}"
      x=${pos.x} y=${pos.y} text-anchor="middle" dominant-baseline="central"
      transform="rotate(${rotDeg}, ${pos.x}, ${pos.y})"
      @click=${() => this.selectAstroTerm(key)}
    >${this.formatTimeShort(time)}</text>`
  }

  private formatTimeShort(date: Date): string {
    if (isNaN(date.getTime())) return '—'
    const h = date.getHours()
    const m = date.getMinutes()
    const ampm = h >= 12 ? 'p' : 'a'
    const h12 = h % 12 || 12
    return `${h12}:${m.toString().padStart(2, '0')}${ampm}`
  }

  private renderSunIcon(x: number, y: number, size: number): unknown {
    return svg`<text
      x=${x} y=${y}
      text-anchor="middle" dominant-baseline="central"
      font-family="'Material Symbols Outlined'"
      font-size="${size}px"
      fill="var(--cairn-text-muted-color, #94a3b8)"
    >light_mode</text>`
  }

  private renderMoonPhaseSvg(
    phase: number,
    fraction: number,
    x: number,
    y: number,
    r: number
  ): unknown {
    const dark = 'var(--cairn-text-muted-color, #94a3b8)'
    const light = 'var(--cairn-surface-color, #ffffff)'
    if (fraction < 0.02)
      return svg`<circle cx=${x} cy=${y} r=${r} fill="none" stroke=${dark} stroke-width="1" />`
    if (fraction > 0.98) return svg`<circle cx=${x} cy=${y} r=${r} fill=${dark} />`

    const isWaxing = phase < 0.5
    const outerSweep = isWaxing ? 1 : 0
    const tRx = Math.abs(1 - 2 * fraction) * r
    const tSweep = fraction < 0.5 ? outerSweep : 1 - outerSweep

    return svg`
      <circle cx=${x} cy=${y} r=${r} fill=${light} stroke=${dark} stroke-width="0.5" />
      <path
        d="M ${x} ${y - r} A ${r} ${r} 0 0 ${outerSweep} ${x} ${y + r} A ${tRx} ${r} 0 0 ${tSweep} ${x} ${y - r}"
        fill=${dark}
      />
    `
  }

  private renderExpandedDefs(_prefix: string, keys: string[]): unknown {
    if (!this.activeAstroKey || !keys.includes(this.activeAstroKey)) return nothing
    const event = ASTRO_EVENTS[this.activeAstroKey]
    return html`<div class="arc-def"><strong>${event.label}</strong> — ${event.description}</div>`
  }

  private computeNowAngle(now: Date, events: Array<{ time: Date; a: number }>): number {
    const t = now.getTime()
    for (let i = 0; i < events.length - 1; i++) {
      const cur = events[i]
      const next = events[i + 1]
      const ct = cur.time.getTime()
      const nt = next.time.getTime()
      if (isNaN(ct) || isNaN(nt)) continue
      if (t >= ct && t <= nt) {
        const frac = (t - ct) / (nt - ct)
        return cur.a + frac * (next.a - cur.a)
      }
    }
    return NaN
  }

  private renderNowDot(angle: number, cx: number, cy: number, r: number): unknown {
    if (isNaN(angle)) return nothing
    const pos = this.arcPoint(angle, cx, cy, r)
    return svg`<circle cx=${pos.x} cy=${pos.y} r="4"
      fill="var(--cairn-text-color, #1e293b)" />`
  }

  private renderAstroTab(): unknown {
    const now = new Date()
    const times = SunCalc.getTimes(now, this.lat, this.lng)
    const moonTimes = SunCalc.getMoonTimes(now, this.lat, this.lng)
    const moonIllum = SunCalc.getMoonIllumination(now)

    return html`
      ${this.renderSunArc(times, now)}
      <div class="astro-separator"></div>
      ${this.renderMoonArc(moonTimes, moonIllum, now)}
    `
  }

  private renderSunArc(times: ReturnType<typeof SunCalc.getTimes>, now: Date): unknown {
    const CX = 140
    const CY = 100
    const R = 70
    const SW = 4
    const LABEL_R = 93
    const STEP = Math.PI / 18

    const events = [
      { key: 'nightEnd', time: times.nightEnd, a: Math.PI + 3 * STEP },
      { key: 'nauticalDawn', time: times.nauticalDawn, a: Math.PI + 2 * STEP },
      { key: 'dawn', time: times.dawn, a: Math.PI + STEP },
      { key: 'sunrise', time: times.sunrise, a: Math.PI },
      { key: 'solarNoon', time: times.solarNoon, a: Math.PI / 2 },
      { key: 'sunset', time: times.sunset, a: 0 },
      { key: 'dusk', time: times.dusk, a: -STEP },
      { key: 'nauticalDusk', time: times.nauticalDusk, a: -2 * STEP },
      { key: 'night', time: times.night, a: -3 * STEP },
    ]

    const validEvents = events.filter(e => !isNaN(e.time.getTime()))
    const arcColor = 'var(--cairn-text-muted-color, #94a3b8)'
    const arcStart = this.arcPoint(Math.PI + 3 * STEP, CX, CY, R)
    const arcEnd = this.arcPoint(-3 * STEP, CX, CY, R)
    const nowAngle = this.computeNowAngle(now, validEvents)

    return html`
      <div class="astro-diagram">
        <svg viewBox="0 0 280 155">
          ${svg`
            <path
              d="M ${arcStart.x} ${arcStart.y} A ${R} ${R} 0 1 1 ${arcEnd.x} ${arcEnd.y}"
              stroke=${arcColor} stroke-width=${SW} fill="none"
            />
            ${validEvents.map(e => this.renderArcTick(e.a, CX, CY, R, SW / 2))}
            ${validEvents.map(e => this.renderArcLabel(e.key, e.time, e.a, CX, CY, LABEL_R))}
            ${this.renderSunIcon(CX, CY, 36)}
            ${this.renderNowDot(nowAngle, CX, CY, R)}
          `}
        </svg>
        ${this.renderExpandedDefs(
          'sun',
          events.map(e => e.key)
        )}
      </div>
    `
  }

  private renderMoonArc(
    moonTimes: ReturnType<typeof SunCalc.getMoonTimes>,
    moonIllum: ReturnType<typeof SunCalc.getMoonIllumination>,
    now: Date
  ): unknown {
    const phaseName = this.moonPhaseName(moonIllum.phase)
    const illumPct = Math.round(moonIllum.fraction * 100)

    const rise = moonTimes.rise as Date | undefined
    const set = moonTimes.set as Date | undefined

    if (!rise && !set) {
      return html`
        <div class="astro-diagram">
          <div style="text-align: center; padding: 8px 0;">
            <svg width="36" height="36" viewBox="0 0 36 36" style="display: inline-block;">
              ${this.renderMoonPhaseSvg(moonIllum.phase, moonIllum.fraction, 18, 18, 16)}
            </svg>
          </div>
          <div class="moon-phase-label">${phaseName} · ${illumPct}% illuminated</div>
          <div class="moon-phase-label">
            ${moonTimes.alwaysUp ? 'Moon is up all day' : 'Moon does not rise today'}
          </div>
        </div>
      `
    }

    if (rise && set) {
      const CX = 140
      const CY = 90
      const R = 55
      const SW = 4
      const LABEL_R = 75
      const moonCenter = this.arcPoint(Math.PI / 2, CX, CY, R * 0.4)
      const arcColor = 'var(--cairn-text-muted-color, #94a3b8)'
      const moonNowAngle = this.computeNowAngle(now, [
        { time: rise, a: Math.PI },
        { time: set, a: 0 },
      ])

      return html`
        <div class="astro-diagram">
          <svg viewBox="0 0 280 100">
            ${svg`
              <path
                d="M ${CX - R} ${CY} A ${R} ${R} 0 1 1 ${CX + R} ${CY}"
                stroke=${arcColor} stroke-width=${SW} fill="none"
              />
              ${this.renderArcTick(Math.PI, CX, CY, R, SW / 2)}
              ${this.renderArcTick(0, CX, CY, R, SW / 2)}
              ${this.renderArcLabel('moonrise', rise, Math.PI, CX, CY, LABEL_R)}
              ${this.renderArcLabel('moonset', set, 0, CX, CY, LABEL_R)}
              ${this.renderMoonPhaseSvg(moonIllum.phase, moonIllum.fraction, moonCenter.x, moonCenter.y, 14)}
              ${this.renderNowDot(moonNowAngle, CX, CY, R)}
            `}
          </svg>
          <div class="moon-phase-label">${phaseName} · ${illumPct}% illuminated</div>
          ${this.renderExpandedDefs('moon', ['moonrise', 'moonset'])}
        </div>
      `
    }

    const singleTime = (rise ?? set)!
    const singleLabel = rise ? 'Moonrise' : 'Moonset'
    return html`
      <div class="astro-diagram">
        <div style="text-align: center; padding: 8px 0;">
          <svg width="36" height="36" viewBox="0 0 36 36" style="display: inline-block;">
            ${this.renderMoonPhaseSvg(moonIllum.phase, moonIllum.fraction, 18, 18, 16)}
          </svg>
        </div>
        <div class="moon-phase-label">${phaseName} · ${illumPct}% illuminated</div>
        <div class="moon-phase-label">${singleLabel}: ${this.formatTime(singleTime)}</div>
      </div>
    `
  }

  // ── Hazards Tab ──

  private renderHazardsTab(): unknown {
    if (this.hazardsLoading) {
      return html`<div class="loading">
        <div class="spinner"></div>
        Loading hazard data...
      </div>`
    }

    const hasAvy = this.avalancheZone != null
    const hasLightning = this.getHazardLightningRisk() !== 'low'
    const hasFire = this.hazardFireFeatures.length > 0
    const hasNps = this.hazardNpsAlerts.length > 0
    const hasAny = hasAvy || hasLightning || hasFire || hasNps

    return html`
      ${hasAvy ? this.renderAvyHazardSection() : nothing}
      ${hasLightning ? this.renderLightningHazardSection() : nothing}
      ${hasFire ? this.renderFireHazardSection() : nothing}
      ${hasNps ? this.renderNpsHazardSection() : nothing}
      ${!hasAny ? html`<div class="no-data">No active hazards for this location</div>` : nothing}
    `
  }

  private getHazardLightningRisk(): LightningRisk {
    if (!this.weatherData) return 'low'
    const now = Date.now()
    const end = now + 24 * 60 * 60 * 1000
    let peakCape = 0
    for (const h of this.weatherData.hourly) {
      const t = new Date(h.time).getTime()
      if (t >= now && t <= end && h.cape > peakCape) {
        peakCape = h.cape
      }
    }
    return capeToLightningRisk(peakCape)
  }

  private renderAvyHazardSection(): unknown {
    const zone = this.avalancheZone
    if (!zone) return nothing

    const isOffSeason = zone.off_season
    const level = zone.danger_level

    const hasElevationDanger =
      zone.danger_upper != null || zone.danger_middle != null || zone.danger_lower != null

    const dateRange =
      zone.start_date && zone.end_date
        ? `${new Date(zone.start_date).toLocaleDateString()} – ${new Date(zone.end_date).toLocaleDateString()}`
        : null

    return html`
      <div class="section-label">
        Avalanche Risk
        ${zone.link
          ? html`<a
              href=${zone.link}
              target="_blank"
              rel="noopener"
              style="float:right;font-size:0.7rem;font-weight:normal"
              >${zone.center_id}</a
            >`
          : nothing}
      </div>
      <div class="compact-item" style="border-bottom:none">
        ${this.renderDangerIcon(level, 20)}
        <div class="compact-body">
          <div class="compact-title-row">
            <span class="compact-title">
              ${hasElevationDanger && level >= 1
                ? html`<span
                    class="avy-popover-trigger"
                    @click=${() => this.toggleAvyPopover('elevation')}
                    >${dangerLabel(level as DangerLevel)}</span
                  >`
                : dangerLabel(level as DangerLevel)}
              — ${zone.name}
              ${isOffSeason
                ? html`<span
                    style="color:var(--cairn-text-secondary-color, #64748b);font-weight:normal"
                    >(Ended)</span
                  >`
                : nothing}
            </span>
          </div>
          ${dateRange ? html`<div class="compact-sub">${dateRange}</div>` : nothing}
        </div>
      </div>
      ${zone.travel_advice && !isOffSeason
        ? html`<div
            style="font-size:0.75rem;color:var(--cairn-text-secondary-color, #64748b);margin-top:2px"
          >
            ${zone.travel_advice}
          </div>`
        : nothing}
      ${zone.forecast_problems.length > 0
        ? html`
            <div style="font-size:0.75rem;margin-top:2px">
              ${zone.forecast_problems.map(
                (p, i) => html`
                  <div style="margin-bottom:2px">
                    <span
                      class="avy-popover-trigger"
                      @click=${() => this.toggleAvyPopover(`problem-${i}`)}
                      >${problemLabel(p.type as AvalancheProblemType)}</span
                    >
                    ${p.likelihood
                      ? html`<span style="color:var(--cairn-text-secondary-color, #64748b)">
                          — ${p.likelihood}</span
                        >`
                      : nothing}
                    ${p.size
                      ? html`<span style="color:var(--cairn-text-muted-color, #94a3b8)">
                          (${this.humanizeSize(p.size)})</span
                        >`
                      : nothing}
                  </div>
                `
              )}
            </div>
          `
        : html`<div
            style="font-size:0.75rem;color:var(--cairn-text-muted-color, #94a3b8);margin-top:2px"
          >
            No problems reported
          </div>`}
      ${zone.bottom_line
        ? html`<details class="avy-details" style="font-size:0.75rem;margin-top:4px">
            <summary class="avy-details-summary">
              <span>Description</span>
              <span class="material-icons avy-details-caret">chevron_right</span>
            </summary>
            <div class="compact-sub" style="margin-top:2px;font-style:italic">
              ${zone.bottom_line}
            </div>
          </details>`
        : nothing}
      ${this.avyPopoverKey ? this.renderAvyPopover(zone) : nothing}
    `
  }

  private toggleAvyPopover(key: string): void {
    this.avyPopoverKey = this.avyPopoverKey === key ? null : key
  }

  private renderAvyPopover(zone: AvalancheZoneProperties): unknown {
    const key = this.avyPopoverKey
    if (!key) return nothing

    let content: unknown = nothing

    if (key === 'elevation') {
      content = this.renderElevationDanger(zone)
    } else if (key.startsWith('problem-')) {
      const idx = parseInt(key.split('-')[1])
      const problem = zone.forecast_problems[idx]
      if (problem) content = this.renderProblemPopoverContent(problem)
    }

    return html`
      <div class="avy-popover-overlay" @click=${() => (this.avyPopoverKey = null)}>
        <div class="avy-popover" @click=${(e: Event) => e.stopPropagation()}>
          <button class="avy-popover-close" @click=${() => (this.avyPopoverKey = null)}>
            &times;
          </button>
          ${content}
        </div>
      </div>
    `
  }

  private renderProblemPopoverContent(problem: {
    type: string
    likelihood: string | null
    size: string | null
    locations: string[]
  }): unknown {
    return html`
      <div style="font-size:0.75rem;font-weight:600;margin-bottom:8px">
        ${problemLabel(problem.type as AvalancheProblemType)}
      </div>
      <div style="display:flex;gap:12px;align-items:flex-start">
        <div style="flex:1">${this.renderProblemRose(problem.locations)}</div>
        ${problem.likelihood || problem.size
          ? html`<div style="display:flex;gap:8px">
              ${problem.likelihood
                ? html`<div style="text-align:center">
                    <div
                      style="font-size:0.65rem;color:var(--cairn-text-secondary-color, #64748b);margin-bottom:2px"
                    >
                      Likelihood
                    </div>
                    <svg
                      width="90"
                      height="120"
                      viewBox="0 0 90 120"
                      style="color:var(--cairn-text-color, #1e293b)"
                    >
                      ${this.renderLikelihoodScale(problem.likelihood)}
                    </svg>
                  </div>`
                : nothing}
              ${problem.size
                ? html`<div style="text-align:center">
                    <div
                      style="font-size:0.65rem;color:var(--cairn-text-secondary-color, #64748b);margin-bottom:2px"
                    >
                      Size
                    </div>
                    <svg
                      width="90"
                      height="120"
                      viewBox="0 0 90 120"
                      style="color:var(--cairn-text-color, #1e293b)"
                    >
                      ${this.renderSizeScale(problem.size)}
                    </svg>
                  </div>`
                : nothing}
            </div>`
          : nothing}
      </div>
    `
  }

  private renderElevationDanger(zone: AvalancheZoneProperties): unknown {
    const level = zone.danger_level
    const alpLevel = (zone.danger_upper ?? level) as DangerLevel
    const tlnLevel = (zone.danger_middle ?? level) as DangerLevel
    const btlLevel = (zone.danger_lower ?? level) as DangerLevel

    const alpColor = dangerColor(alpLevel)
    const tlnColor = dangerColor(tlnLevel)
    const btlColor = dangerColor(btlLevel)

    return html`
      <div style="display:flex;gap:12px;align-items:center">
        <svg width="80" height="70" viewBox="0 0 160 140" style="flex-shrink:0">
          <polygon points="80,6 56,50 104,50" fill="${alpColor}" stroke="#333" stroke-width="1" />
          <polygon
            points="56,50 32,94 128,94 104,50"
            fill="${tlnColor}"
            stroke="#333"
            stroke-width="1"
          />
          <polygon
            points="32,94 8,134 152,134 128,94"
            fill="${btlColor}"
            stroke="#333"
            stroke-width="1"
          />
        </svg>
        <div style="font-size:0.7rem;line-height:2.2">
          <div style="display:flex;align-items:center;gap:4px">
            ${this.renderDangerIcon(alpLevel, 16)} Alpine: ${dangerLabel(alpLevel)}
          </div>
          <div style="display:flex;align-items:center;gap:4px">
            ${this.renderDangerIcon(tlnLevel, 16)} Treeline: ${dangerLabel(tlnLevel)}
          </div>
          <div style="display:flex;align-items:center;gap:4px">
            ${this.renderDangerIcon(btlLevel, 16)} Below: ${dangerLabel(btlLevel)}
          </div>
        </div>
      </div>
    `
  }

  private renderDangerIcon(level: number, size = 18): unknown {
    const iconMap: Record<number, string> = {
      1: 'low.png',
      2: 'moderate.png',
      3: 'considerable.png',
      4: 'high.png',
      5: 'extreme.png',
    }
    const src = iconMap[level]
    if (!src) {
      const color = dangerColor(level as DangerLevel)
      return html`<span
        style="display:inline-block;width:${size}px;height:${size}px;border-radius:2px;background:${color};flex-shrink:0"
      ></span>`
    }
    return html`<img
      src="/icons/avy-danger/${src}"
      alt="${dangerLabel(level as DangerLevel)}"
      width="${size}"
      height="${size}"
      style="flex-shrink:0;object-fit:contain"
    />`
  }

  private renderProblemRose(locations: string[]): unknown {
    if (locations.length === 0)
      return html`<span style="color:var(--cairn-text-secondary-color, #64748b);font-size:0.75rem"
        >No aspect data available</span
      >`

    const ASPECT_MAP: Record<string, string> = {
      north: 'N',
      northeast: 'NE',
      east: 'E',
      southeast: 'SE',
      south: 'S',
      southwest: 'SW',
      west: 'W',
      northwest: 'NW',
    }
    const ELEV_MAP: Record<string, string> = {
      upper: 'above-treeline',
      middle: 'near-treeline',
      lower: 'below-treeline',
    }

    const locationSet = new Set<string>()
    for (const loc of locations) {
      const parts = loc.toLowerCase().split(' ')
      const elev = parts.pop()!
      const aspect = parts.join('')
      const mappedAspect = ASPECT_MAP[aspect]
      const mappedElev = ELEV_MAP[elev]
      if (mappedAspect && mappedElev) locationSet.add(`${mappedAspect}:${mappedElev}`)
    }

    const size = 140
    const cx = size / 2
    const cy = size / 2
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
    const rings = [
      { band: 'above-treeline', r1: 0, r2: 18 },
      { band: 'near-treeline', r1: 18, r2: 36 },
      { band: 'below-treeline', r1: 36, r2: 52 },
    ]
    const edgeAngles = directions.map((_, i) => ((i * 45 - 90 + 22.5) * Math.PI) / 180)

    const wedges: unknown[] = []
    for (const ring of rings) {
      for (let i = 0; i < 8; i++) {
        const dir = directions[i]
        const filled = locationSet.has(`${dir}:${ring.band}`)
        const eL = edgeAngles[(i + 7) % 8]
        const eR = edgeAngles[i]

        let path: string
        if (ring.r1 === 0) {
          const x1 = cx + ring.r2 * Math.cos(eL)
          const y1 = cy + ring.r2 * Math.sin(eL)
          const x2 = cx + ring.r2 * Math.cos(eR)
          const y2 = cy + ring.r2 * Math.sin(eR)
          path = `M${cx},${cy} L${x1},${y1} L${x2},${y2} Z`
        } else {
          const xoL = cx + ring.r2 * Math.cos(eL)
          const yoL = cy + ring.r2 * Math.sin(eL)
          const xoR = cx + ring.r2 * Math.cos(eR)
          const yoR = cy + ring.r2 * Math.sin(eR)
          const xiR = cx + ring.r1 * Math.cos(eR)
          const yiR = cy + ring.r1 * Math.sin(eR)
          const xiL = cx + ring.r1 * Math.cos(eL)
          const yiL = cy + ring.r1 * Math.sin(eL)
          path = `M${xoL},${yoL} L${xoR},${yoR} L${xiR},${yiR} L${xiL},${yiL} Z`
        }
        wedges.push(
          svg`<path d="${path}" fill="${filled ? '#1e3a5f' : '#f1f5f9'}" stroke="#94a3b8" stroke-width="0.5" />`
        )
      }
    }

    const labelRadius = 62
    const labels = directions.map((dir, i) => {
      const angle = ((i * 45 - 90) * Math.PI) / 180
      const lx = cx + labelRadius * Math.cos(angle)
      const ly = cy + labelRadius * Math.sin(angle)
      return svg`<text x="${lx}" y="${ly}" text-anchor="middle" dominant-baseline="central" font-size="9" style="fill:var(--cairn-text-color, #1e293b)" font-weight="700">${dir}</text>`
    })

    return html`<svg
      width="${size}"
      height="${size}"
      viewBox="0 0 ${size} ${size}"
      style="display:block;margin:0 auto"
    >
      ${wedges} ${labels}
    </svg>`
  }

  private humanizeSize(size: string): string {
    const nums =
      size
        .replace(/[Dd]/g, '')
        .match(/\d+\.?\d*/g)
        ?.map(Number) ?? []
    if (nums.length === 0) return size
    const hi = Math.max(...nums)
    if (hi <= 1) return 'Small'
    if (hi <= 2) return 'Small to Moderate'
    if (hi <= 3) return 'Large'
    if (hi <= 4) return 'Very Large'
    return 'Historic'
  }

  private renderLikelihoodScale(value: string): unknown {
    const levels = ['Certain', 'Very Likely', 'Likely', 'Possible', 'Unlikely']
    const h = 120
    const topPad = 8
    const botPad = 8
    const lineX = 14
    const trackH = h - topPad - botPad
    const step = trackH / (levels.length - 1)

    const norm = value.toLowerCase().trim()
    let activeIdx = levels.findIndex(l => l.toLowerCase() === norm)
    if (activeIdx === -1) activeIdx = levels.findIndex(l => norm.includes(l.toLowerCase()))
    if (activeIdx === -1) activeIdx = levels.findIndex(l => l.toLowerCase().includes(norm))

    const ticks = levels.map((label, i) => {
      const y = topPad + i * step
      const isActive = i === activeIdx
      return svg`
        <line x1="${lineX - 4}" y1="${y}" x2="${lineX + 4}" y2="${y}"
          stroke="currentColor" stroke-width="${isActive ? 2 : 1}" opacity="${isActive ? 1 : 0.4}" />
        <text x="${lineX + 12}" y="${y}" dominant-baseline="central"
          font-size="${isActive ? 9 : 7.5}" font-weight="${isActive ? 700 : 400}"
          fill="currentColor" opacity="${isActive ? 1 : 0.5}">${label}</text>
        ${
          isActive
            ? svg`<rect x="${lineX - 5}" y="${y - 5}" width="10" height="10" rx="2" fill="currentColor" />`
            : nothing
        }
      `
    })

    return svg`
      <line x1="${lineX}" y1="${topPad}" x2="${lineX}" y2="${topPad + trackH}"
        stroke="currentColor" stroke-width="1" opacity="0.3" />
      ${ticks}
    `
  }

  private renderSizeScale(value: string): unknown {
    const ticks = [
      { label: 'D5 - Historic', n: 5 },
      { label: 'D4 - Very Lg', n: 4 },
      { label: 'D3 - Large', n: 3 },
      { label: 'D2 - Small', n: 2 },
      { label: 'D1 - Small', n: 1 },
    ]

    const nums =
      value
        .replace(/[Dd]/g, '')
        .match(/\d+\.?\d*/g)
        ?.map(Number) ?? []
    const lo = nums.length > 0 ? Math.min(...nums) : 0
    const hi = nums.length > 0 ? Math.max(...nums) : 0

    const h = 120
    const topPad = 8
    const botPad = 8
    const lineX = 14
    const trackH = h - topPad - botPad
    const step = trackH / (ticks.length - 1)

    const valToY = (n: number): number => topPad + ((5 - n) / 4) * trackH

    const tickEls = ticks.map(({ label, n }, i) => {
      const y = topPad + i * step
      const inRange = lo > 0 && n >= Math.floor(lo) && n <= Math.ceil(hi)
      return svg`
        <line x1="${lineX - 4}" y1="${y}" x2="${lineX + 4}" y2="${y}"
          stroke="currentColor" stroke-width="${inRange ? 2 : 1}" opacity="${inRange ? 1 : 0.4}" />
        <text x="${lineX + 12}" y="${y}" dominant-baseline="central"
          font-size="${inRange ? 9 : 7.5}" font-weight="${inRange ? 700 : 400}"
          fill="currentColor" opacity="${inRange ? 1 : 0.5}">${label}</text>
      `
    })

    const boxW = 10
    const markers =
      lo > 0
        ? svg`<rect x="${lineX - boxW / 2}" y="${valToY(hi)}"
          width="${boxW}" height="${Math.max(valToY(lo) - valToY(hi), 2)}"
          rx="2" fill="currentColor" />`
        : nothing

    return svg`
      <line x1="${lineX}" y1="${topPad}" x2="${lineX}" y2="${topPad + trackH}"
        stroke="currentColor" stroke-width="1" opacity="0.3" />
      ${markers} ${tickEls}
    `
  }

  private renderLightningHazardSection(): unknown {
    const risk = this.getHazardLightningRisk()
    if (risk === 'low') return nothing

    const color = risk === 'high' ? '#ef4444' : '#eab308'
    return html`
      <div class="section-label">Lightning Risk</div>
      <div class="compact-item">
        <span class="material-icons" style="color:${color};font-size:20px">bolt</span>
        <div class="compact-body">
          <div class="compact-title-row">
            <span class="compact-title"
              >${risk === 'high' ? 'High' : 'Moderate'} lightning risk</span
            >
          </div>
          <div class="compact-sub">Next 24 hours based on atmospheric instability (CAPE)</div>
        </div>
      </div>
    `
  }

  private renderFireHazardSection(): unknown {
    if (this.hazardFireFeatures.length === 0) return nothing

    const FIRE_TYPE_LABELS: Record<string, { label: string; icon: string; color: string }> = {
      'wfigs-fires': { label: 'Active Wildfire', icon: 'local_fire_department', color: '#dc2626' },
      'nps-restrictions': { label: 'Fire Restriction (NPS)', icon: 'warning', color: '#ea580c' },
      'usfws-restrictions': {
        label: 'Fire Restriction (USFWS)',
        icon: 'warning',
        color: '#d97706',
      },
    }

    const firmsHotspots = this.hazardFireFeatures.filter(h => h.fireType === 'firms-hotspots')
    const otherFeatures = this.hazardFireFeatures.filter(h => h.fireType !== 'firms-hotspots')

    const seen = new Set<string>()

    return html`
      <div class="section-label">Fire Activity</div>
      ${otherFeatures
        .filter(h => {
          const p = h.properties
          const key = `${h.fireType}:${(p.name as string) ?? ''}:${JSON.stringify(h.feature.geometry)}`
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
        .map(h => {
          const p = h.properties
          const meta = FIRE_TYPE_LABELS[h.fireType] ?? {
            label: h.fireType,
            icon: 'warning',
            color: '#888',
          }
          const details: Array<{ label: string; value: string }> = []

          if (h.fireType === 'wfigs-fires') {
            if (p.acres != null)
              details.push({ label: 'Size', value: `${Number(p.acres).toLocaleString()} acres` })
            if (p.percentContained != null)
              details.push({ label: 'Contained', value: `${p.percentContained as number}%` })
            if (p.discoveryDate)
              details.push({
                label: 'Discovered',
                value: new Date(p.discoveryDate as string).toLocaleDateString(),
              })
            if (p.state) details.push({ label: 'State', value: p.state as string })
          } else if (h.fireType === 'nps-restrictions') {
            if (p.restriction)
              details.push({ label: 'Restriction', value: p.restriction as string })
          } else if (h.fireType === 'usfws-restrictions') {
            if (p.status) details.push({ label: 'Status', value: p.status as string })
            if (p.stage) details.push({ label: 'Stage', value: p.stage as string })
          }

          if (h.distanceKm != null && h.distanceKm > 0.1) {
            details.push({ label: 'Distance', value: `${h.distanceKm.toFixed(1)} km` })
          }

          const title = (p.name as string | undefined) ?? meta.label

          return html`
            <div class="compact-item">
              <span class="material-icons" style="color:${meta.color};font-size:18px"
                >${meta.icon}</span
              >
              <div class="compact-body">
                <div class="compact-title-row">
                  <span class="compact-title">${title}</span>
                </div>
                ${title !== meta.label
                  ? html`<div class="compact-sub">${meta.label}</div>`
                  : nothing}
                ${details.map(
                  d => html`<div class="compact-sub"><strong>${d.label}:</strong> ${d.value}</div>`
                )}
              </div>
            </div>
          `
        })}
      ${this.renderFirmsSummary(firmsHotspots)}
    `
  }

  private renderNpsHazardSection(): unknown {
    if (this.hazardNpsAlerts.length === 0) return nothing

    return html`
      <div class="section-label">Park Alerts</div>
      ${this.hazardNpsAlerts.map(alert => {
        const color = alert.category === 'Danger' ? '#dc2626' : '#d97706'
        return html`
          <div class="compact-item">
            <div class="emergency-dot" style="background:${color}"></div>
            <div class="compact-body">
              <div class="compact-title-row">
                <span class="compact-title">${alert.title}</span>
              </div>
              ${alert.description
                ? html`<div class="compact-sub" style="line-height:1.3">${alert.description}</div>`
                : nothing}
              <div class="compact-sub">
                <span
                  style="display:inline-block;padding:1px 4px;border-radius:3px;background:${color};color:#fff;font-size:0.65rem"
                  >${alert.category}</span
                >
                ${alert.parkName
                  ? html`<span style="margin-left:4px">${alert.parkName}</span>`
                  : nothing}
                ${alert.distanceKm > 0.1
                  ? html`<span style="margin-left:4px;opacity:0.7"
                      >${alert.distanceKm.toFixed(1)} km</span
                    >`
                  : nothing}
                ${alert.url
                  ? html`<a
                      href="${alert.url}"
                      target="_blank"
                      rel="noopener"
                      style="margin-left:4px"
                      >More info</a
                    >`
                  : nothing}
              </div>
            </div>
          </div>
        `
      })}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'map-context-popup': MapContextPopup
  }
}
